import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconArrowRight,
  IconBrackets,
  IconChartBar,
  IconClipboardList,
  IconCode,
  IconDatabase,
  IconGavel,
  IconInfoCircle,
  IconListDetails,
  IconMathFunction,
  IconPlaylistAdd,
  IconScale,
  IconSettings,
  IconTemplate,
  IconTimeline,
  IconTournament,
  IconTrophy,
  IconUsers,
  IconUserStar,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  flowStep: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderLeft: `2px solid ${theme.colors.cyan[7]}`,
    marginLeft: rem(10),
    "&:last-of-type": {
      borderLeft: "none",
    },
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

interface GqlBlockProps {
  readonly children: string;
}

const GqlBlock = ({ children }: GqlBlockProps) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

interface SectionHeaderProps {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly id: string;
}

const SectionHeader = ({ icon, title, id }: SectionHeaderProps) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const TournamentDocs = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Tournament Documentation"
        subtitle="Complete reference for the tournament management system"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#lifecycle">Tournament Lifecycle</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#creation">Creating a Tournament</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#registration">Registration & Teams</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#steps">Steps & Brackets</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#scoring">Scoring & Matches</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#admins">Admin Permissions</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#settings">Settings Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#statuses">Status Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#rule-engine">Rule Engine</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#presets">Presets</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#lua-preset-scripts">Lua Preset Scripts</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#builtin-templates">Built-in Templates</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#rule-scoring">Rule Scoring System</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#advancement-rules">Advancement Rules</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#expressions">Expression Language</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#structure-templates">Structure Templates</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#cross-step-rules">Cross-Step Rules</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#rule-engine-api">Rule Engine API</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTrophy size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The tournament system is a comprehensive competitive event
            management feature that handles the full lifecycle of a tournament:
            from creation and configuration, through player registration,
            bracket generation, team seeding, match scoring, and final results.
          </Text>
          <Text mb="md">
            Tournaments are composed of a hierarchy of nested entities:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Tournament
  +-- Configuration (registration dates, team sizes, custom fields, rules)
  +-- Teams[] (registered/confirmed teams of players)
  +-- Steps[] (tournament stages: qualifications, playoffs, etc.)
       +-- Groups[] (pools of teams that compete against each other)
            +-- Rounds[] (sets of games within a group, e.g. semi-finals)
                 +-- Games[] (individual matchups between teams)
                      +-- Matches[] (individual confrontations with scores)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Each step is independent. Teams only move between steps through
            automation rules or manual seeding. This allows tournament
            organizers to mix different formats (e.g. Round-Robin qualifications
            followed by Single Elimination playoffs).
          </Alert>
        </div>

        {/* ===================== LIFECYCLE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Tournament Lifecycle"
            id="lifecycle"
          />
          <Text mb="md">
            A tournament progresses through a series of date-driven states. The
            console automatically derives the current status based on configured
            dates.
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`DRAFT -----> UPCOMING -----> REGISTRATION -----> LIVE -----> FINISHED
  |               |                |                |             |
  No visibleAt    Before reg       Reg dates are    Tournament    Tournament
  or not yet      start date       active           startAt       endAt
  visible                                           reached       reached`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Status Derivation Logic
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Text>
                <Badge color="gray" variant="light" size="sm" mr="xs">
                  draft
                </Badge>
                No <Code>visibleAt</Code> set, or current time is before{" "}
                <Code>visibleAt</Code>
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="blue" variant="light" size="sm" mr="xs">
                  upcoming
                </Badge>
                Visible but registration hasn't started yet (before{" "}
                <Code>startRegistrationsAt</Code>)
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="cyan" variant="light" size="sm" mr="xs">
                  registration
                </Badge>
                Between <Code>startRegistrationsAt</Code> and{" "}
                <Code>endRegistrationsAt</Code>
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="green" variant="light" size="sm" mr="xs">
                  live
                </Badge>
                Current time is after <Code>startAt</Code> but before{" "}
                <Code>endAt</Code>
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="dark" variant="light" size="sm" mr="xs">
                  finished
                </Badge>
                Current time is after <Code>endAt</Code>
              </Text>
            </List.Item>
          </List>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            These statuses are derived client-side from dates. The server does
            not store a tournament "status" field. All status logic is computed
            in <Code>deriveTournamentStatus()</Code>.
          </Alert>
        </div>

        {/* ===================== CREATION ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconClipboardList size={18} />}
            title="Creating a Tournament"
            id="creation"
          />
          <Text mb="md">
            Creating a tournament is a multi-step process. First you create the
            tournament with basic information, then configure settings
            incrementally.
          </Text>

          <Title order={4} mb="xs">
            Step 1: Basic Information
          </Title>
          <Text mb="sm">
            Navigate to <Code>/tournaments/new</Code>. Required fields:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Title</strong> - Tournament name (string, required)
            </List.Item>
            <List.Item>
              <strong>Description</strong> - Rich text content (TipTap editor,
              required)
            </List.Item>
            <List.Item>
              <strong>Start At</strong> - Tournament start date (required)
            </List.Item>
            <List.Item>
              <strong>End At</strong> - Tournament end date (required)
            </List.Item>
          </List>
          <Text mb="md" size="sm" c="dimmed">
            On creation, you are redirected to the tournament dashboard. The{" "}
            <Code>visibleAt</Code> field is only available when editing an
            existing tournament.
          </Text>

          <GqlBlock>
            {`mutation ($title: String!, $description: String!, $startAt: DateTime!, $endAt: DateTime!) {
  createTournament(input: {
    title: $title
    description: $description
    startAt: $startAt
    endAt: $endAt
  }) {
    id
  }
}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Step 2: Configure Registration
          </Title>
          <Text mb="sm">
            Navigate to <Code>Settings &gt; Registration</Code>. Fields:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Registrations Open At / Close At</strong> - Date range for
              accepting registrations
            </List.Item>
            <List.Item>
              <strong>Min / Max Team Size</strong> - Player count limits per
              team
            </List.Item>
            <List.Item>
              <strong>Teams Count</strong> - Maximum number of teams allowed
            </List.Item>
            <List.Item>
              <strong>Team Status After Registration</strong> -{" "}
              <Code>REGISTERED</Code> (auto-confirm) or{" "}
              <Code>AWAITING_FOR_PRESENCE_CONFIRMATION</Code> (requires manual
              admin approval)
            </List.Item>
          </List>

          <Title order={4} mt="lg" mb="xs">
            Step 3: Configure Registration Rules (Optional)
          </Title>
          <Text mb="sm">
            Define conditions that players and teams must meet to register. Two
            types of conditions:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Member Conditions
              </Text>
              <Text size="sm" mb="xs">
                Applied to each individual player. Supports:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Property source: Player profile or Identity Provider
                </List.Item>
                <List.Item>Property state: EXISTS or DON'T_EXIST</List.Item>
                <List.Item>
                  Value conditions: numeric (BT, BTE, EQ, LT, LTE, NEQ) or
                  string (EQ, NEQ)
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Team Conditions
              </Text>
              <Text size="sm" mb="xs">
                Applied to the team as a whole. Additional features:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Aggregation types for numeric conditions (e.g. average, sum
                  across team members)
                </List.Item>
                <List.Item>
                  Aggregation property source (Player or IDP)
                </List.Item>
                <List.Item>
                  Same value condition types as member conditions
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            <strong>Example:</strong> Require a League of Legends account with
            Diamond+ rank AND a Twitch channel with 1,000+ followers. Create a
            member condition with IDP source "League of Legends", property
            "rank", numeric condition BTE = 4 (Diamond), and another for
            "Twitch" IDP, property "followers", numeric condition BTE = 1000.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Step 4: Custom Fields (Optional)
          </Title>
          <Text mb="sm">Add custom data fields to teams. Each field has:</Text>
          <List spacing="xs" size="sm">
            <List.Item>
              <strong>Name</strong> - Display name
            </List.Item>
            <List.Item>
              <strong>Property</strong> - Internal key (used in API filters)
            </List.Item>
            <List.Item>
              <strong>Type</strong> - STRING, NUMBER, BOOLEAN, DATE, etc.
            </List.Item>
            <List.Item>
              <strong>Order</strong> - Display order
            </List.Item>
            <List.Item>
              <strong>Visibility</strong> - PUBLIC, OWNER, OWNER_OR_PERMISSION,
              or WITH_PERMISSION
            </List.Item>
            <List.Item>
              <strong>Editability</strong> - ALWAYS, ONE_TIME, or WITH_PERMISSION
            </List.Item>
            <List.Item>
              <strong>Required / Unique</strong> - Validation constraints
            </List.Item>
          </List>
        </div>

        {/* ===================== REGISTRATION & TEAMS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsers size={18} />}
            title="Registration & Teams"
            id="registration"
          />
          <Text mb="md">
            Teams are the primary participant entity. The teams page provides
            comprehensive management with advanced filtering capabilities.
          </Text>

          <Title order={4} mb="xs">
            Team Status Flow
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`REGISTERED ----> AWAITING_FOR_PRESENCE_CONFIRMATION ----> CONFIRMED
     |                          |                               |
     +--- DECLINED              +--- DECLINED                   +--- ELIMINATED
                                                                (during tournament)`}
            </GqlBlock>
          </Card>
          <List spacing="xs" mb="md">
            <List.Item>
              <Badge color="blue" variant="light" size="sm" mr="xs">
                REGISTERED
              </Badge>
              Team has submitted registration
            </List.Item>
            <List.Item>
              <Badge color="yellow" variant="light" size="sm" mr="xs">
                AWAITING_FOR_PRESENCE_CONFIRMATION
              </Badge>
              Waiting for manual admin approval
            </List.Item>
            <List.Item>
              <Badge color="green" variant="light" size="sm" mr="xs">
                CONFIRMED
              </Badge>
              Team is confirmed to participate
            </List.Item>
            <List.Item>
              <Badge color="red" variant="light" size="sm" mr="xs">
                DECLINED
              </Badge>
              Team was rejected by admin
            </List.Item>
            <List.Item>
              <Badge color="dark" variant="light" size="sm" mr="xs">
                ELIMINATED
              </Badge>
              Team was eliminated during the tournament
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Team Management Features
          </Title>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="filters">
              <Accordion.Control>
                <Group gap="xs">
                  <IconListDetails size={16} />
                  <Text fw={500}>Advanced Filtering</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  All filters are debounced (400ms) to avoid excessive API
                  calls. Active filters are displayed as removable badges.
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Status filter</strong> - Filter by team status
                    (dropdown)
                  </List.Item>
                  <List.Item>
                    <strong>Player username</strong> - Search teams by player
                    name (text input)
                  </List.Item>
                  <List.Item>
                    <strong>Team custom fields</strong> - Filter by any
                    configured team custom field property/value
                  </List.Item>
                  <List.Item>
                    <strong>Player custom fields</strong> - Filter by player
                    profile properties or identity provider data (e.g. "Riot
                    Games &rarr; rank")
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="status-change">
              <Accordion.Control>
                <Group gap="xs">
                  <IconArrowRight size={16} />
                  <Text fw={500}>Status Changes</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Click any team's status badge to open a dropdown menu with all
                  available status transitions. A confirmation modal appears
                  before applying the change. The mutation used is{" "}
                  <Code>updateTournamentTeamStatus</Code>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="manual-team">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsers size={16} />
                  <Text fw={500}>Manual Team Creation</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  The "Register Team" button opens a modal to manually create a
                  team:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>Team name and tag</List.Item>
                  <List.Item>Initial status selection</List.Item>
                  <List.Item>
                    Player search autocomplete to add members
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  Uses the <Code>registerCustomTournamentTeams</Code> mutation.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="generate-teams">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsers size={16} />
                  <Text fw={500}>Generate Random Teams</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  The "Generate Teams" button creates randomized test teams for
                  development/testing:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    Teams count (1-100) and members per team (1-5)
                  </List.Item>
                  <List.Item>Initial team status</List.Item>
                  <List.Item>
                    Auto-generates random names, tags, and player profiles
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="expansion">
              <Accordion.Control>
                <Group gap="xs">
                  <IconDatabase size={16} />
                  <Text fw={500}>Team Row Expansion</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Click any team row to expand it and view detailed information:
                  custom field values, member list with usernames linked to
                  player profiles, player custom fields as badges, and
                  collapsible identity provider data sections per player.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== STEPS & BRACKETS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconChartBar size={18} />}
            title="Steps & Brackets"
            id="steps"
          />
          <Text mb="md">
            Steps represent independent stages of the tournament. Each step has
            its own bracket structure, teams, and scoring system.
          </Text>

          <Title order={4} mb="xs">
            Step Types
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="red">SINGLE_ELIM</Badge>
              </Group>
              <Text size="sm">
                Single elimination bracket. Teams are eliminated after one loss.
                Configure "Best Of" (number of matches to win a game). System
                auto-generates the bracket tree with a single group.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="blue">SCORE</Badge>
              </Group>
              <Text size="sm">
                Score-based competition. Uses the group/round editor to
                configure pools and rounds manually. Suitable for point-based or
                custom scoring formats. <strong>Required</strong> for the{" "}
                <Code>battle-royale</Code>, <Code>points-race</Code>, and{" "}
                <Code>cumulative-rounds-series</Code> presets so the seeder
                populates every round (not just round 1) with the
                participating teams.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="gray">ROUND_ROBIN / DOUBLE_ELIM / CUSTOM</Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Round-robin groups, double elimination brackets, and fully
                custom configurations. Currently locked/under development.
              </Text>
            </Card>
          </SimpleGrid>

          <Alert
            icon={<IconInfoCircle />}
            color="cyan"
            variant="light"
            mb="md"
            title="Choosing a step type for a preset"
          >
            <Text size="sm">
              The step <Code>type</Code> is what the seeder reads when it
              decides how to place teams into the generated structure:
            </Text>
            <List size="sm" mt="xs">
              <List.Item>
                <Code>SCORE</Code> &mdash; required for scoring formats that
                expect every round to hold the full roster. Use this for{" "}
                <Code>battle-royale</Code>, <Code>points-race</Code>, and{" "}
                <Code>cumulative-rounds-series</Code>.
              </List.Item>
              <List.Item>
                <Code>SINGLE_ELIM</Code> / <Code>DOUBLE_ELIM</Code> &mdash;
                bracket presets that consume the next-power-of-2 bracket
                layout.
              </List.Item>
              <List.Item>
                <Code>CUSTOM</Code> &mdash; used for formats with bespoke
                group / round structures that the preset builds explicitly
                (Swiss, round-robin, GSL, gauntlet, step-ladder,
                king-of-the-hill, ...).
              </List.Item>
            </List>
          </Alert>

          <Title order={4} mb="xs">
            Step Status Flow
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`CONFIGURED ---> GENERATING ---> GENERATED ---> SEEDING ---> SEEDED ---> STARTED ---> ENDED
      |              |                |             |              |            |
      Edit freely    System is        Select &      System is      Start the    All games
      Configure      generating       seed teams    applying       step (no     completed
      groups/rounds  brackets         into brackets seeding        more edits)`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Step Management Flow
          </Title>
          <List type="ordered" spacing="sm" mb="md">
            <List.Item>
              <Text>
                <strong>Create Step</strong> - Select type, configure name,
                order, and either "Best Of" (for elimination) or groups/rounds
                (for others)
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Generate</strong> - Set team count and generate the
                bracket structure. Requires at least one group with one round.
                Uses <Code>generateTournamentStep</Code> mutation.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Seed Teams</strong> - Select confirmed teams and
                configure seeding mechanism (e.g. Random) and group repartition
                (e.g. Balanced). Uses <Code>seedTournamentStep</Code> mutation.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Start Step</strong> - Lock the step and begin the
                competition. Uses <Code>startTournamentStep</Code> mutation. No
                further configuration edits are possible after starting.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Score Matches</strong> - Click teams in the bracket to
                update scores and match statuses.
              </Text>
            </List.Item>
          </List>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            Updating a step that has already been generated will reset all
            generated content (brackets, seeding, scores). A confirmation
            warning is shown before allowing this.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Group & Round Editor
          </Title>
          <Text mb="sm">
            For non-elimination step types, the form includes a dynamic
            group/round editor:
          </Text>
          <List spacing="xs" size="sm">
            <List.Item>
              <strong>Groups</strong> - Named pools (accordion). Each has a
              name, description, and nested rounds. Add/delete groups.
            </List.Item>
            <List.Item>
              <strong>Rounds</strong> - Named rounds within each group. Each has
              a name, order, and game configuration. Add/delete rounds.
            </List.Item>
            <List.Item>
              <strong>Game Config</strong> - Per-round: teams count, "Best Of"
              (<Code>wonMatchCountToWinGame</Code>), and match configuration
              (score formula, variables).
            </List.Item>
          </List>

          <Alert
            icon={<IconInfoCircle />}
            color="yellow"
            variant="light"
            mt="sm"
          >
            <strong>Legacy field.</strong> <Code>wonMatchCountToWinGame</Code>{" "}
            is deprecated. For new tournaments, use a{" "}
            <Code>MATCH_SERIES_RESOLUTION</Code> advancement rule instead. This
            field is kept for legacy tournaments without rule sets.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Bracket Visualization
          </Title>
          <Text mb="sm">
            The step dashboard renders different views depending on the step
            type:
          </Text>
          <List spacing="xs" size="sm">
            <List.Item>
              <strong>SingleElimBracket</strong> - Used for SINGLE_ELIM and
              DOUBLE_ELIM. Tree structure with round columns, game cards,
              connector lines, and team progression indicators.
            </List.Item>
            <List.Item>
              <strong>GroupStageView</strong> - Used for ROUND_ROBIN, SCORE, and
              CUSTOM. Shows pools/groups with all teams, round-by-round display,
              and match results.
            </List.Item>
          </List>
          <Text size="sm" mt="xs" c="dimmed">
            Both views support clicking on teams to open the score update modal
            (only when step status is STARTED).
          </Text>
        </div>

        {/* ===================== SCORING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBrackets size={18} />}
            title="Scoring & Matches"
            id="scoring"
          />
          <Text mb="md">
            The scoring system operates at the match level. Each game contains
            one or more matches, and teams need to win a configured number of
            matches to win the game.
          </Text>

          <Title order={4} mb="xs">
            Match Score Status
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Status</th>
                <th>Description</th>
                <th>Visual</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>WAITING</Code>
                </td>
                <td>Match has not been played yet</td>
                <td>
                  <Badge color="dark" variant="light">
                    Waiting
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>WINNER</Code>
                </td>
                <td>Team won this match</td>
                <td>
                  <Badge color="green" variant="light">
                    Winner
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>LOSER</Code>
                </td>
                <td>Team lost this match</td>
                <td>
                  <Badge color="red" variant="light">
                    Loser
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>FORFEIT</Code>
                </td>
                <td>Team forfeited the match</td>
                <td>
                  <Badge color="red" variant="light">
                    Forfeit
                  </Badge>
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Update Score Modal
          </Title>
          <Text mb="sm" size="sm">
            When a step is STARTED, clicking a team in the bracket opens the
            Update Score Modal:
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>Shows team name and member list</List.Item>
            <List.Item>Current status badge</List.Item>
            <List.Item>Score input (numeric, min 0)</List.Item>
            <List.Item>
              Status select (WINNER, LOSER, FORFEIT, WAITING)
            </List.Item>
          </List>

          <GqlBlock>
            {`mutation ($matchId: ID!, $input: [UpdateMatchScoreInput!]!) {
  updateMatchScores(matchId: $matchId, input: $input) {
    id
  }
}

# Input per team:
# { teamId: ID!, forcedScoreValue: Float!, status: MatchScoreStatus! }`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            When a team accumulates enough match wins to reach the{" "}
            <Code>wonMatchCountToWinGame</Code> threshold, the game
            automatically ends and teams progress to the next game based on the
            bracket structure (winning game → next round, losing game → losers
            bracket or elimination).
          </Alert>

          <Alert
            icon={<IconInfoCircle />}
            color="yellow"
            variant="light"
            mt="sm"
          >
            <strong>Legacy field.</strong> <Code>wonMatchCountToWinGame</Code>{" "}
            is deprecated. For new tournaments, use a{" "}
            <Code>MATCH_SERIES_RESOLUTION</Code> advancement rule instead. This
            field is kept for legacy tournaments without rule sets.
          </Alert>
        </div>

        {/* ===================== ADMINS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUserStar size={18} />}
            title="Admin Permissions"
            id="admins"
          />
          <Text mb="md">
            Tournament admins can be granted granular permissions to manage
            specific aspects of a tournament. Admins are added by searching for
            player accounts and assigning one or more permissions.
          </Text>

          <Title order={4} mb="xs">
            Operations
          </Title>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <strong>Add Admin</strong> - Search for a player, select
              permissions from the <Code>TournamentAdminPermissions</Code> enum,
              and upsert
            </List.Item>
            <List.Item>
              <strong>Update Admin</strong> - Re-upsert with modified
              permissions (same mutation)
            </List.Item>
            <List.Item>
              <strong>Remove Admin</strong> - Delete via the table's delete
              action
            </List.Item>
          </List>

          <GqlBlock>
            {`# Query admins
query tournamentAdmins($tournamentId: ID!) {
  tournamentAdmins(tournamentId: $tournamentId) {
    tournamentId
    permissions
    account { id, email, profiles { username } }
  }
}

# Upsert admin
mutation ($tournamentId: ID!, $accountId: ID!, $permissions: [TournamentAdminPermissions!]!) {
  tournamentAdminUpsert(input: {
    tournamentId: $tournamentId
    accountId: $accountId
    permissions: $permissions
  }) { accountId }
}

# Delete admin
mutation ($tournamentId: ID!, $accountId: ID!) {
  tournamentAdminDelete(input: { tournamentId: $tournamentId, accountId: $accountId })
}`}
          </GqlBlock>
        </div>

        {/* ===================== SETTINGS REFERENCE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Settings Reference"
            id="settings"
          />
          <Text mb="md">
            All settings are updated through the <Code>updateTournament</Code>{" "}
            mutation with the appropriate input fields.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="general">
              <Accordion.Control>General Settings</Accordion.Control>
              <Accordion.Panel>
                <Table striped>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Code>title</Code>
                      </td>
                      <td>String!</td>
                      <td>Tournament display name</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>description</Code>
                      </td>
                      <td>String!</td>
                      <td>Rich text content (HTML)</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>startAt</Code>
                      </td>
                      <td>DateTime!</td>
                      <td>Tournament start date</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>endAt</Code>
                      </td>
                      <td>DateTime!</td>
                      <td>Tournament end date</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>visibleAt</Code>
                      </td>
                      <td>DateTime</td>
                      <td>
                        When the tournament becomes publicly visible (edit-only)
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="registration">
              <Accordion.Control>Registration Settings</Accordion.Control>
              <Accordion.Panel>
                <Table striped>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Code>startRegistrationsAt</Code>
                      </td>
                      <td>DateTime!</td>
                      <td>When registrations open</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>endRegistrationsAt</Code>
                      </td>
                      <td>DateTime!</td>
                      <td>When registrations close</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>configuration.teamMinSize</Code>
                      </td>
                      <td>Number!</td>
                      <td>Minimum players per team</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>configuration.teamMaxSize</Code>
                      </td>
                      <td>Number!</td>
                      <td>Maximum players per team</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>configuration.teamsCount</Code>
                      </td>
                      <td>Number!</td>
                      <td>Maximum teams allowed</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>configuration.teamStatusAfterRegistration</Code>
                      </td>
                      <td>Enum</td>
                      <td>REGISTERED or AWAITING_FOR_PRESENCE_CONFIRMATION</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="custom-fields">
              <Accordion.Control>Custom Fields</Accordion.Control>
              <Accordion.Panel>
                <Table striped>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Code>name</Code>
                      </td>
                      <td>String!</td>
                      <td>Display name</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>property</Code>
                      </td>
                      <td>String!</td>
                      <td>Internal key for API access</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>type</Code>
                      </td>
                      <td>PropertyType!</td>
                      <td>STRING, NUMBER, BOOLEAN, DATE, etc.</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>order</Code>
                      </td>
                      <td>Number!</td>
                      <td>Display order</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>visibility</Code>
                      </td>
                      <td>PropertyVisibility!</td>
                      <td>PUBLIC, OWNER, OWNER_OR_PERMISSION, WITH_PERMISSION</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>editability</Code>
                      </td>
                      <td>ProperyEditability!</td>
                      <td>ALWAYS, ONE_TIME, WITH_PERMISSION</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>required</Code>
                      </td>
                      <td>Boolean!</td>
                      <td>Must be filled in during registration</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>unique</Code>
                      </td>
                      <td>Boolean!</td>
                      <td>Value must be unique across teams</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations used by the tournament
            feature.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="tournament-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Tournament Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      tournaments (list)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float, $status: TournamentsQueryStatus) {
  tournaments(
    page: { first: $count, after: $cursor }
    query: { orderBy: REGISTRATIONS_START_AT, orderDirection: DESC,
             status: $status, showNotVisible: true }
  ) {
    nodes { id, title, startAt, endAt, startRegistrationsAt,
            endRegistrationsAt, visibleAt,
            configuration { teamsCount },
            teams(status: CONFIRMED, page: { first: 1 }) { totalCount } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournament (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  tournament(id: $id) {
    id, title, description, startAt, endAt, visibleAt,
    startRegistrationsAt, endRegistrationsAt,
    configuration {
      teamsCount, teamMinSize, teamMaxSize,
      teamStatusAfterRegistration,
      customFields { name, type, property, order, unique, required,
                     public, editability, visibility },
      registrationConditions { memberConditions { ... }, teamConditions { ... } }
    },
    teams(status: CONFIRMED, page: { first: 1 }) { totalCount }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="tournament-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Tournament Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createTournament
                    </Text>
                    <GqlBlock>
                      {`mutation ($title: String!, $description: String!, $startAt: DateTime!, $endAt: DateTime!) {
  createTournament(input: { title: $title, description: $description,
                            startAt: $startAt, endAt: $endAt }) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateTournament
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $title: String!, $description: String!,
         $startAt: DateTime!, $endAt: DateTime!, $visibleAt: DateTime) {
  updateTournament(id: $id, input: { title: $title, description: $description,
                                      startAt: $startAt, endAt: $endAt,
                                      visibleAt: $visibleAt }) { id }
}

# Also used for configuration updates:
mutation ($id: ID!, $configuration: UpdateTournamentConfigurationOrImportFromIdInput!) {
  updateTournament(id: $id, input: { configuration: $configuration }) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteTournament
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) { deleteTournament(id: $id) }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="team-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    TEAMS
                  </Badge>
                  <Text>Team Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentTeams (list with filters)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float, $tournamentId: ID!,
       $status: TournamentTeamStatus, $propertiesFilter: [PropertyValueInput!],
       $playerUsernameQuery: String, $playerPropertiesFilter: [PropertyValueInput!],
       $playerProfileIds: [ID!]) {
  tournamentTeams(
    page: { first: $count, after: $cursor }, status: $status,
    tournamentId: $tournamentId, propertiesFilter: $propertiesFilter,
    playerUsernameQuery: $playerUsernameQuery,
    playerPropertiesFilter: $playerPropertiesFilter,
    playerProfileIds: $playerProfileIds
  ) {
    nodes { id, name, status }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                    <Text size="sm" c="dimmed" mt="xs">
                      Pass <Code>playerProfileIds</Code> to fetch every
                      tournament team that contains at least one ACCEPTED member
                      whose player profile ID is in the provided list. Useful
                      for looking up every tournament team a given player
                      belongs to.
                    </Text>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentTeam (detail with members)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  tournamentTeam(teamId: $id) {
    id
    customFields { name, value, property }
    members { playerProfileId }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateTournamentTeamStatus
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $status: TournamentTeamStatus!) {
  updateTournamentTeamStatus(tournamentTeamId: $id, status: $status) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      registerCustomTournamentTeams
                    </Text>
                    <GqlBlock>
                      {`mutation ($tournamentId: ID!, $input: RegisterCustomTeamsInput!) {
  registerCustomTournamentTeams(tournamentId: $tournamentId, input: $input) { id }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="step-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    STEPS
                  </Badge>
                  <Text>Step Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentSteps (list)
                    </Text>
                    <GqlBlock>
                      {`query ($tournamentId: ID!) {
  tournamentSteps(tournamentId: $tournamentId) {
    id, name, order, status, type, tournamentId,
    configuration { groups { rounds { name } } }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentStep (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  tournamentStep(id: $id) {
    id, name, description, type, status, order, tournamentId,
    configuration {
      groups {
        name, description,
        rounds {
          name, order,
          game {
            teamsCount, useMatchScoresAsGameScore, wonMatchCountToWinGame,
            matchConfiguration { scoreFormula, variables { ... } }
          }
        }
      }
    }
  }
}`}
                    </GqlBlock>
                    <Alert
                      icon={<IconInfoCircle />}
                      color="yellow"
                      variant="light"
                      mt="xs"
                    >
                      <strong>Legacy field.</strong>{" "}
                      <Code>wonMatchCountToWinGame</Code> is deprecated. For new
                      tournaments, use a <Code>MATCH_SERIES_RESOLUTION</Code>{" "}
                      advancement rule instead. This field is kept for legacy
                      tournaments without rule sets.
                    </Alert>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentStepGeneratedShape (bracket structure)
                    </Text>
                    <GqlBlock>
                      {`query ($stepId: ID!) {
  tournamentStepGeneratedShape(stepId: $stepId) {
    id, name,
    rounds {
      id, name, order,
      games {
        id, order, status, losingGameId, winningGameId,
        configuration { wonMatchCountToWinGame },
        matches { id, order, status }
      }
    }
  }
}`}
                    </GqlBlock>
                    <Alert
                      icon={<IconInfoCircle />}
                      color="yellow"
                      variant="light"
                      mt="xs"
                    >
                      <strong>Legacy field.</strong>{" "}
                      <Code>wonMatchCountToWinGame</Code> is deprecated. For new
                      tournaments, use a <Code>MATCH_SERIES_RESOLUTION</Code>{" "}
                      advancement rule instead. This field is kept for legacy
                      tournaments without rule sets.
                    </Alert>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Step Lifecycle Mutations
                    </Text>
                    <GqlBlock>
                      {`# Create step
mutation ($tournamentId: ID!, $step: CreateTournamentStepInput!,
         $configuration: StepConfigurationInput!) {
  createTournamentStep(tournamentId: $tournamentId,
                       step: $step, configuration: $configuration) { id }
}

# Update step
mutation ($stepId: ID!, $step: CreateTournamentStepInput!,
         $configuration: StepConfigurationInput!) {
  updateTournamentStep(stepId: $stepId, step: $step,
                       configuration: $configuration) { id }
}

# Delete step
mutation ($id: ID!) { deleteTournamentStep(stepId: $id) }

# Generate bracket
mutation ($stepId: ID!, $teamsCount: Int!) {
  generateTournamentStep(stepId: $stepId, teamsCount: $teamsCount)
}

# Seed teams (see "Seeding a step" section below for input shape)
mutation ($input: SeedStepInput!) {
  seedTournamentStep(input: $input) {
    id
    status
    seedingOrder
  }
}

# Update seeding order without re-seeding
mutation ($stepId: ID!, $orderedTeamIds: [ID!]!) {
  updateSeedingOrder(stepId: $stepId, orderedTeamIds: $orderedTeamIds) {
    id
    seedingOrder
  }
}

# Update manual pins without re-seeding
mutation ($stepId: ID!, $pins: [SeedingPinInput!]!) {
  updateSeedingPins(stepId: $stepId, pins: $pins) { id }
}

# Re-seed a step (requires SEEDED status; wipes match state)
mutation ($stepId: ID!) {
  reseedStep(stepId: $stepId) { id status }
}

# Withdraw a team from a step
mutation ($stepId: ID!, $teamId: ID!) {
  withdrawTeamFromStep(stepId: $stepId, teamId: $teamId) { id status }
}

# Start step
mutation ($stepId: ID!) { startTournamentStep(stepId: $stepId) }

# Update match scores
mutation ($matchId: ID!, $input: [UpdateMatchScoreInput!]!) {
  updateMatchScores(matchId: $matchId, input: $input) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Step scores (paginated)
                    </Text>
                    <GqlBlock>
                      {`query ($stepId: ID!, $page: PageInfo!) {
  tournamentStepGroupRoundGameMatchScoresGetForStep(stepId: $stepId, page: $page) {
    pageInfo { endCursor, hasNextPage }
    nodes { teamId, status, score, matchId }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="player-facing-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="violet" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Player-Facing Queries</Text>
                  <Badge color="yellow" variant="light" size="xs">
                    Not used in console
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                >
                  These queries are available in the API but not used in the
                  admin console. They are intended for player-facing
                  applications (tournament pages, player dashboards).
                </Alert>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentPlayers
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List the individual players in a tournament (as opposed to
                      teams), one entry per player. Supports filtering by player
                      properties and username, and ordering by any property.{" "}
                      <Code>status</Code> filters players by the status of the
                      team they belong to (e.g. <Code>CONFIRMED</Code>); omit it
                      to include players across every team status.{" "}
                      <Code>totalCount</Code> reflects the number of matching
                      players. Requires the{" "}
                      <Code>ORGANIZATION_TOURNAMENTS_TEAMS_LIST</Code>{" "}
                      permission.
                    </Text>
                    <GqlBlock>
                      {`query ($tournamentId: ID!, $page: PageInfo!, $status: TournamentTeamStatus,
       $playerUsernameQuery: String, $playerPropertiesFilter: [PropertyValueInput!],
       $orderByProperty: OrderByProperty) {
  tournamentPlayers(
    tournamentId: $tournamentId
    page: $page
    status: $status            # optional — omit to include all team statuses
    playerUsernameQuery: $playerUsernameQuery
    playerPropertiesFilter: $playerPropertiesFilter
    orderByProperty: $orderByProperty
  ) {
    nodes { playerProfileId, status, tournamentTeamId, createdAt, updatedAt,
            team { id, name, status } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournamentTeamInvites
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List all pending team invites for a tournament. Useful for
                      showing a player their incoming invites.
                    </Text>
                    <GqlBlock>
                      {`query ($tournamentId: ID, $page: PageInfo!,
       $status: TournamentTeamMemberStatus) {
  tournamentTeamInvites(
    tournamentId: $tournamentId
    page: $page
    status: $status
  ) {
    nodes { playerProfileId, status, tournamentTeamId, createdAt,
            team { id, name, status } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

# TournamentTeamMemberStatus values: ACCEPTED, AWAITING, DENIED`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      match (single match detail)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch full details of a single match including its
                      configuration and status.
                    </Text>
                    <GqlBlock>
                      {`query ($matchId: ID!) {
  match(matchId: $matchId) {
    id
    order
    status                    # MatchStatus: WAITING, STARTED, ENDED
    tournamentStepGroupRoundGameId
    configuration {
      scoreFormula
      variables { defaultValue, displayIcon, displayName, formulaName }
    }
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerMatchesHistory
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Paginated match history for a specific player across all
                      tournaments. Useful for player profile pages.
                    </Text>
                    <GqlBlock>
                      {`query ($playerId: ID!, $page: PageInfo!) {
  playerMatchesHistory(playerId: $playerId, page: $page) {
    nodes {
      id, order, status,
      tournamentStepGroupRoundGameId,
      configuration { scoreFormula, variables { ... } }
      createdAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournament.myTeam
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Field on the Tournament type that returns the
                      authenticated user's team (if they have one). Useful for
                      player-facing pages to show "My Team" status.
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  tournament(id: $id) {
    myTeam {
      id, name, status, tag,
      members { playerProfileId, status }
      customFields { name, value, property }
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      tournament.teamScores / tournamentStep.teamScores
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Aggregated team scores for the entire tournament or a
                      specific step. Useful for leaderboards and final
                      standings.
                    </Text>
                    <GqlBlock>
                      {`# Tournament-level scores
query ($id: ID!) {
  tournament(id: $id) {
    teamScores {
      teamId
      score
      team { id, name, status }
    }
  }
}

# Step-level scores
query ($id: ID!) {
  tournamentStep(id: $id) {
    teamScores {
      teamId
      score
      team { id, name, status }
    }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="player-facing-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="pink" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Player-Facing Mutations</Text>
                  <Badge color="yellow" variant="light" size="xs">
                    Not used in console
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                >
                  These mutations power the player-facing tournament experience
                  (registration, team invites, attendance confirmation). They
                  are called from generated platform pages, not the admin
                  console.
                </Alert>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      registerTournamentTeam
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Player-initiated team registration. Creates a new team for
                      the authenticated user. Differs from{" "}
                      <Code>registerCustomTournamentTeams</Code> which is
                      admin-only and can set arbitrary members and status.
                    </Text>
                    <GqlBlock>
                      {`mutation ($tournamentId: ID!, $input: RegisterTournamentTeamInput!) {
  registerTournamentTeam(tournamentId: $tournamentId, input: $input) {
    id, name, tag, status,
    members { playerProfileId, status }
  }
}

# RegisterTournamentTeamInput:
# {
#   name: String!
#   tag: String!
#   customFields: [PropertyValueInput!]    # Optional custom field values
#   fromExistingTeamId: ID                 # Optional: clone from existing team
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateTournamentTeam
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update team name, tag, or custom fields. Can be called by
                      the team manager or an admin.
                    </Text>
                    <GqlBlock>
                      {`mutation ($teamId: ID, $tournamentId: ID,
         $input: UpdateTournamentTeamInput!) {
  updateTournamentTeam(
    teamId: $teamId
    tournamentId: $tournamentId
    input: $input
  ) {
    id, name, tag, status,
    customFields { name, value, property }
  }
}

# UpdateTournamentTeamInput:
# {
#   name: String!
#   tag: String!
#   customFields: [PropertyValueInput!]
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      inviteTournamentTeamMember
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Invite a player to join the authenticated user's team in a
                      tournament.
                    </Text>
                    <GqlBlock>
                      {`mutation ($tournamentId: ID!, $playerId: ID!) {
  inviteTournamentTeamMember(
    tournamentId: $tournamentId
    playerId: $playerId
  )
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      answerToTournamentTeamInvite
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Accept or decline a team invite. Called by the invited
                      player.
                    </Text>
                    <GqlBlock>
                      {`mutation ($teamId: ID!, $accept: Boolean!) {
  answerToTournamentTeamInvite(teamId: $teamId, accept: $accept)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteTournamentTeamInvite
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Cancel a pending team invite. Called by the team manager.
                    </Text>
                    <GqlBlock>
                      {`mutation ($teamId: ID!, $playerId: ID!) {
  deleteTournamentTeamInvite(teamId: $teamId, playerId: $playerId)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      confirmTournamentAttendance
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Player confirms or declines attendance after their team
                      has been confirmed. Used when teams require presence
                      confirmation.
                    </Text>
                    <GqlBlock>
                      {`mutation ($tournamentId: ID!, $attendance: Boolean!) {
  confirmTournamentAttendance(
    tournamentId: $tournamentId
    attendance: $attendance
  ) {
    id, name, status
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      confirmTournamentTeam
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Admin confirms or rejects a team that is awaiting
                      validation. Alternative to using{" "}
                      <Code>updateTournamentTeamStatus</Code>.
                    </Text>
                    <GqlBlock>
                      {`mutation ($teamId: ID!, $confirm: Boolean!) {
  confirmTournamentTeam(teamId: $teamId, confirm: $confirm) {
    id, name, status
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteTournamentTeam
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Player deletes their own team from a tournament. Requires
                      the authenticated user to be the team manager.
                    </Text>
                    <GqlBlock>
                      {`mutation ($tournamentId: ID!) {
  deleteTournamentTeam(tournamentId: $tournamentId)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteAnyTournamentTeam
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Force-delete any team regardless of ownership. Requires
                      elevated permissions (tournament admin with MANAGE_TEAMS
                      or organization admin).
                    </Text>
                    <GqlBlock>
                      {`mutation ($teamId: ID!) {
  deleteAnyTournamentTeam(teamId: $teamId)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Tournament Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateTournamentInput {
  title: String!
  description: String!
  startAt: DateTime
  endAt: DateTime
  startRegistrationsAt: DateTime
  endRegistrationsAt: DateTime
  visibleAt: DateTime
  configuration: UpdateTournamentConfigurationOrImportFromIdInput
  tournamentSeriesId: ID
}

input UpdateTournamentInput {
  title: String
  description: String
  startAt: DateTime
  endAt: DateTime
  startRegistrationsAt: DateTime
  endRegistrationsAt: DateTime
  visibleAt: DateTime
  configuration: UpdateTournamentConfigurationOrImportFromIdInput
}

input UpdateTournamentConfigurationOrImportFromIdInput {
  configuration: UpdateTournamentConfigurationInput
  fromId: ID    # Import configuration from another tournament
}

input UpdateTournamentConfigurationInput {
  teamsCount: Float
  teamMinSize: Float
  teamMaxSize: Float
  teamStatusAfterRegistration: TeamStatusAfterRegistration
  customFields: [PropertyInput!]
  registrationConditions: RegistrationConditionsInput
  type: ConfigurationType = TOURNAMENT
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Team Registration Inputs
                    </Text>
                    <GqlBlock>
                      {`# Player-facing registration
input RegisterTournamentTeamInput {
  name: String!
  tag: String!
  customFields: [PropertyValueInput!]
  fromExistingTeamId: ID
}

# Admin custom team registration
input RegisterCustomTeamsInput {
  customTeams: [RegisterCustomTeamInput!]!
}

input RegisterCustomTeamInput {
  name: String!
  tag: String!
  status: TournamentTeamStatus!
  members: [CustomTeamMemberInput!]
  customFields: [PropertyValueInput!]
  fromExistingTeamId: ID
}

input CustomTeamMemberInput {
  playerId: ID             # Use existing player
  player: CreatePlayerInput  # Or create a new player on the fly
}

input UpdateTournamentTeamInput {
  name: String!
  tag: String!
  customFields: [PropertyValueInput!]
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Step Configuration Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateTournamentStepInput {
  name: String!
  description: String!
  order: Float!
  type: StepType!
  properties: [PropertyValueInput!]
}

input StepConfigurationInput {
  type: ConfigurationType! = STEP
  groups: [GroupConfigurationInput!]!
}

input GroupConfigurationInput {
  name: String!
  description: String
  rounds: [RoundConfigurationInput!]!
}

input RoundConfigurationInput {
  name: String!
  order: Float!
  game: GameConfigurationInput!
}

input GameConfigurationInput {
  teamsCount: Float!
  wonMatchCountToWinGame: Float!
  useMatchScoresAsGameScore: Boolean!
  matchConfiguration: MatchConfigurationInput!
}

input MatchConfigurationInput {
  scoreFormula: String
  variables: [MatchVariableInput!]!
}

input MatchVariableInput {
  formulaName: String!
  displayName: String!
  displayIcon: String
  defaultValue: Float!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Seeding Inputs
                    </Text>
                    <GqlBlock>
                      {`input SeedStepInput {
  stepId: ID!
  orderedTeamIds: [ID!]!        # Permutation of the step's teams
  pins: [SeedingPinInput!]      # Optional manual pin overrides
}

input SeedingPinInput {
  teamId: ID!                   # Team the pin anchors
  groupId: ID                   # Anchor to a group (distribution step)
  gameId: ID                    # Anchor to a specific game (placement step)
  slotIndex: Int                # Optional 0-based slot within the game
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Score & Condition Inputs
                    </Text>
                    <GqlBlock>
                      {`input UpdateMatchScoreInput {
  teamId: ID!
  status: MatchScoreStatus
  forcedScoreValue: Float
  variables: [UpdateTournamentMatchScoreVariablesInput!]
}

input UpdateTournamentMatchScoreVariablesInput {
  formulaName: String!
  value: Float!
}

input RegistrationConditionsInput {
  memberConditions: [PlayerConditionInput!]!
  teamConditions: [TeamConditionInput!]!
}

input PlayerConditionInput {
  condition: ConditionInput!
  propertySource: PlayerConditionDataSource!
  propertySourceId: ID
  errorMessage: String         # Custom error shown to players
  ruleDescription: String      # Human-readable rule description
}

input ConditionInput {
  property: String!
  propertyCondition: PropertyCondition!
  numericCondition: NumberConditionInput
  stringCondition: StringConditionInput
}

input TeamConditionInput {
  property: String!
  propertyCondition: PropertyCondition!
  errorMessage: String
  numericCondition: TeamNumericConditionInput
  stringCondition: StringConditionInput
}

input TeamNumericConditionInput {
  conditionType: NumberConditionType!
  value: Float!
  aggregationType: AggregationType    # AVG, MAX, MIN, SUM
  propertySource: PlayerConditionDataSource
  propertySourceId: ID
}

input TournamentsQuery {
  status: TournamentsQueryStatus! = ALL
  orderBy: TournamentsQueryOrderBy! = START_AT
  orderDirection: OrderDirection! = ASC
  showNotVisible: Boolean
  filterByPlayers: [TournamentsQueryPlayerIdWithTeamStatus!]
}

input TournamentsQueryPlayerIdWithTeamStatus {
  playerId: ID!
  teamStatus: TournamentTeamStatus!
  memberStatus: TournamentTeamMemberStatus!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== SEEDING, PINNING, WITHDRAWAL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsers size={18} />}
            title="Seeding, Pinning, Withdrawal"
            id="seeding-pinning-withdrawal"
          />
          <Text mb="md">
            Once a step has been generated (bracket structure exists) it must
            be <strong>seeded</strong> before it can start. Seeding assigns
            teams to groups, rounds, and game slots. The preset&apos;s{" "}
            <Code>SEEDING</Code> advancement rules drive the actual placement
            — the mutations below just record the caller&apos;s inputs.
          </Text>

          <Stack gap="md">
            <div>
              <Title order={4} mb="xs">
                Seeding a step
              </Title>
              <Text size="sm" mb="xs">
                Call <Code>seedTournamentStep</Code> with a permutation of the
                step&apos;s teams in the desired seed order and any optional
                manual pins. Pins anchor specific teams to a group, a game, or
                a specific slot within a game; the preset&apos;s repartition /
                placement helpers respect these pins. The step must be in
                <Code>GENERATED</Code> or <Code>SEEDED</Code> status — calling
                the mutation again re-runs the seeding pipeline (idempotent
                re-seed).
              </Text>
              <GqlBlock>
                {`mutation ($input: SeedStepInput!) {
  seedTournamentStep(input: $input) {
    id
    status        # Transitions: GENERATED -> SEEDING -> SEEDED
    seedingOrder  # Echoes the ordered team IDs you supplied
  }
}

# Variables
{
  "input": {
    "stepId": "abc123",
    "orderedTeamIds": ["team-A", "team-B", "team-C", "team-D"],
    "pins": [
      { "teamId": "team-A", "groupId": "group-1" },
      { "teamId": "team-B", "gameId": "game-2", "slotIndex": 0 }
    ]
  }
}`}
              </GqlBlock>
              <Text size="xs" c="dimmed" mt="xs">
                <strong>Errors:</strong>{" "}
                <Code>TOURNAMENT_STEP_NOT_FOUND</Code> (step doesn&apos;t
                belong to the caller&apos;s organization),{" "}
                <Code>TOURNAMENT_STEP_WRONG_STATE</Code> (step is not in
                GENERATED or SEEDED status), or a validation error if
                <Code>orderedTeamIds</Code> is not a valid permutation of the
                step&apos;s teams.
              </Text>
            </div>

            <Divider />

            <div>
              <Title order={4} mb="xs">
                Pinning teams
              </Title>
              <Text size="sm" mb="xs">
                Pins are manual overrides that anchor a team to a specific
                location in the bracket, overriding the preset&apos;s default
                distribution / placement logic for that team. Three shapes are
                supported, evaluated in order of specificity:
              </Text>
              <List size="sm" mb="xs">
                <List.Item>
                  <strong>Group pin</strong> (<Code>groupId</Code> only): the
                  team is guaranteed to land in the named group. The rest of
                  the group is populated by the preset&apos;s repartition
                  helper (<Code>balanced</Code>, <Code>snake</Code>,{" "}
                  <Code>explicit</Code>, <Code>entry_only</Code>).
                </List.Item>
                <List.Item>
                  <strong>Game pin</strong> (<Code>gameId</Code> without{" "}
                  <Code>slotIndex</Code>): the team is placed in the specified
                  entry-round game. The preset chooses the slot.
                </List.Item>
                <List.Item>
                  <strong>Slot pin</strong> (<Code>gameId</Code> +{" "}
                  <Code>slotIndex</Code>): the team is placed in the exact
                  slot. Highest specificity — takes precedence over group
                  pins.
                </List.Item>
              </List>
              <Text size="sm" mb="xs">
                Pins can be updated independently of the seeding order via{" "}
                <Code>updateSeedingPins</Code>. Changing pins does <em>not</em>{" "}
                re-seed the step — call <Code>reseedStep</Code> (while in
                SEEDED) or <Code>seedTournamentStep</Code> to apply them.
              </Text>
              <GqlBlock>
                {`mutation ($stepId: ID!, $pins: [SeedingPinInput!]!) {
  updateSeedingPins(stepId: $stepId, pins: $pins) { id }
}

# Clear all pins
mutation ($stepId: ID!) {
  updateSeedingPins(stepId: $stepId, pins: []) { id }
}`}
              </GqlBlock>
              <Alert
                color="blue"
                icon={<IconInfoCircle />}
                variant="light"
                mt="xs"
              >
                A preset&apos;s pinning support is declared in its metadata
                (<Code>supportsPinning</Code>,{" "}
                <Code>supportsGroupPinning</Code>,{" "}
                <Code>supportsSlotPinning</Code> on{" "}
                <Code>BuiltinPresetModel</Code>). If a preset has{" "}
                <Code>supportsPinning: false</Code>, pins submitted for steps
                using it will be silently ignored or rejected at validation —
                the console hides the pinning UI in that case.
              </Alert>
            </div>

            <Divider />

            <div>
              <Title order={4} mb="xs">
                Re-seeding a step
              </Title>
              <Text size="sm" mb="xs">
                After edits to the seeding order or pins (via{" "}
                <Code>updateSeedingOrder</Code> /{" "}
                <Code>updateSeedingPins</Code>), call <Code>reseedStep</Code>{" "}
                to re-run the seeding pipeline using the currently persisted
                inputs. The step must be in <Code>SEEDED</Code> status; it
                briefly returns to <Code>GENERATED</Code> and then back to{" "}
                <Code>SEEDED</Code>.
              </Text>
              <GqlBlock>
                {`mutation ($stepId: ID!) {
  reseedStep(stepId: $stepId) { id status }
}`}
              </GqlBlock>
              <Alert
                color="yellow"
                icon={<IconAlertCircle />}
                variant="light"
                mt="xs"
              >
                <strong>Destructive within the step.</strong> Re-seeding wipes
                any unsaved match results. Only safe to run before the step
                has <Code>STARTED</Code>. Cross-step side effects (teams
                already advanced to downstream steps) are NOT reverted — those
                must be handled manually.
              </Alert>
            </div>

            <Divider />

            <div>
              <Title order={4} mb="xs">
                Withdrawing a team
              </Title>
              <Text size="sm" mb="xs">
                <Code>withdrawTeamFromStep</Code> flips the team&apos;s status
                to <Code>WITHDRAWN</Code> and fires the{" "}
                <Code>TEAM_WITHDREW</Code> rule-engine event. The
                preset&apos;s <Code>WITHDRAWAL_RESPONSE</Code> rule decides
                what happens next — typical responses include full re-seed
                (pre-start), bye-advance the opponent, forfeit remaining
                matches, or bye-pair the withdrawn team for future rounds
                (Swiss).
              </Text>
              <Text size="sm" mb="xs">
                The mutation is idempotent: calling it on an already-withdrawn
                team is a no-op and returns the current step state. Permission
                is gated on{" "}
                <Code>ORGANIZATION_TOURNAMENTS_TEAMS_UPDATE</Code>.
              </Text>
              <GqlBlock>
                {`mutation ($stepId: ID!, $teamId: ID!) {
  withdrawTeamFromStep(stepId: $stepId, teamId: $teamId) {
    id
    status
  }
}`}
              </GqlBlock>
              <Text size="xs" c="dimmed" mt="xs">
                Built-in withdrawal responses (shipped in the prelude):{" "}
                <Code>add_bracket_withdrawal_rule</Code> (single / double /
                triple elimination),{" "}
                <Code>add_scheduled_withdrawal_rule</Code> (round-robin,
                Swiss, league), and{" "}
                <Code>add_full_roster_withdrawal_rule</Code> (battle-royale,
                points race). Custom presets can author their own
                <Code>WITHDRAWAL_RESPONSE</Code> rule directly.
              </Text>
            </div>
          </Stack>
        </div>

        {/* ===================== STATUS REFERENCE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Status Reference"
            id="statuses"
          />

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="sm">
                Tournament Status (client-derived)
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        draft
                      </Badge>
                    </td>
                    <td>Not yet visible</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="blue" variant="light">
                        upcoming
                      </Badge>
                    </td>
                    <td>Visible, before registration</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="cyan" variant="light">
                        registration
                      </Badge>
                    </td>
                    <td>Registration period active</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        live
                      </Badge>
                    </td>
                    <td>Tournament in progress</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="dark" variant="light">
                        finished
                      </Badge>
                    </td>
                    <td>Tournament completed</td>
                  </tr>
                </tbody>
              </Table>
            </Card>

            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="sm">
                Team Status (server)
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="blue" variant="light">
                        REGISTERED
                      </Badge>
                    </td>
                    <td>Submitted registration</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="yellow" variant="light">
                        AWAITING_FOR_PRESENCE_CONFIRMATION
                      </Badge>
                    </td>
                    <td>Pending admin presence check</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="orange" variant="light">
                        AWAITING_FOR_PAYMENT
                      </Badge>
                    </td>
                    <td>Pending payment</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        CONFIRMED
                      </Badge>
                    </td>
                    <td>Ready to participate</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        DECLINED
                      </Badge>
                    </td>
                    <td>Rejected by admin</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        DENIED
                      </Badge>
                    </td>
                    <td>Registration denied</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="dark" variant="light">
                        ELIMINATED
                      </Badge>
                    </td>
                    <td>Eliminated from tournament</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        NOT_ATTENDING
                      </Badge>
                    </td>
                    <td>Team will not attend</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        NOT_VALID
                      </Badge>
                    </td>
                    <td>Registration invalid</td>
                  </tr>
                </tbody>
              </Table>
            </Card>

            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="sm">
                Step Status (server)
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        CONFIGURED
                      </Badge>
                    </td>
                    <td>Ready to generate</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="yellow" variant="light">
                        GENERATING
                      </Badge>
                    </td>
                    <td>Bracket generation in progress</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="blue" variant="light">
                        GENERATED
                      </Badge>
                    </td>
                    <td>Ready for team seeding</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="yellow" variant="light">
                        SEEDING
                      </Badge>
                    </td>
                    <td>Seeding in progress</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="cyan" variant="light">
                        SEEDED
                      </Badge>
                    </td>
                    <td>Ready to start</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        STARTED
                      </Badge>
                    </td>
                    <td>Competition in progress</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="dark" variant="light">
                        ENDED
                      </Badge>
                    </td>
                    <td>All games completed</td>
                  </tr>
                </tbody>
              </Table>
            </Card>

            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="sm">
                Match Score Status (server)
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="dark" variant="light">
                        WAITING
                      </Badge>
                    </td>
                    <td>Not played yet</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        WINNER
                      </Badge>
                    </td>
                    <td>Team won the match</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        LOSER
                      </Badge>
                    </td>
                    <td>Team lost the match</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        FORFEIT
                      </Badge>
                    </td>
                    <td>Team forfeited</td>
                  </tr>
                </tbody>
              </Table>
            </Card>
          </SimpleGrid>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key TypeScript types and enums used throughout the tournament
            module. All types are generated from the GraphQL schema via codegen.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      StepType
                    </Text>
                    <GqlBlock>
                      {`SCORE
ROUND_ROBIN
SINGLE_ELIM
DOUBLE_ELIM
CUSTOM`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      StepStatus
                    </Text>
                    <GqlBlock>
                      {`CONFIGURED
GENERATING
GENERATED
SEEDING
SEEDED
STARTED
ENDED`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      TournamentTeamStatus
                    </Text>
                    <GqlBlock>
                      {`REGISTERED
AWAITING_FOR_PRESENCE_CONFIRMATION
AWAITING_FOR_PAYMENT
CONFIRMED
DECLINED
DENIED
ELIMINATED
NOT_ATTENDING
NOT_VALID`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      MatchScoreStatus
                    </Text>
                    <GqlBlock>
                      {`WAITING
WINNER
LOSER
FORFEIT`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      TournamentGameStatus
                    </Text>
                    <GqlBlock>
                      {`WAITING
STARTED
ENDED`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      AdvancementRulePurpose
                    </Text>
                    <Text size="xs" c="dimmed" mb="xs">
                      Used by <Code>SEEDING</Code> /{" "}
                      <Code>WITHDRAWAL_RESPONSE</Code> rules authored in
                      presets. See the Lua reference for usage.
                    </Text>
                    <GqlBlock>
                      {`ADVANCEMENT
ELIMINATION
MATCH_SERIES_RESOLUTION
SEEDING
WINNER_PROMOTION
WITHDRAWAL_RESPONSE`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      TournamentTeamMemberStatus
                    </Text>
                    <GqlBlock>
                      {`ACCEPTED
AWAITING
DENIED`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      TeamStatusAfterRegistration
                    </Text>
                    <GqlBlock>
                      {`AWAITING_FOR_PAYMENT
AWAITING_FOR_PRESENCE_CONFIRMATION
CONFIRMED
REGISTERED`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      PropertyType
                    </Text>
                    <GqlBlock>
                      {`STRING
NUMBER
BOOLEAN
DATE
COUNTRY
EMAIL
PHONE
URL`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      AggregationType
                    </Text>
                    <GqlBlock>
                      {`AVG
MAX
MIN
SUM`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      MatchStatus
                    </Text>
                    <GqlBlock>
                      {`WAITING
STARTED
ENDED`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Condition Types
                    </Text>
                    <GqlBlock>
                      {`# NumberConditionType
BT    (Bigger than)
BTE   (Bigger than or equal)
EQ    (Equal)
LT    (Less than)
LTE   (Less than or equal)
NEQ   (Not equal)

# StringConditionType
EQ    (Equal)
NEQ   (Not equal)

# PropertyCondition
EXISTS
DONT_EXIST`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="entities">
              <Accordion.Control>Entity Hierarchy</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`Tournament
├── id: ID!
├── title: String!
├── description: String!
├── startAt: DateTime!
├── endAt: DateTime!
├── visibleAt: DateTime
├── startRegistrationsAt: DateTime
├── endRegistrationsAt: DateTime
├── configuration: TournamentConfiguration
│   ├── teamsCount: Int
│   ├── teamMinSize: Int
│   ├── teamMaxSize: Int
│   ├── teamStatusAfterRegistration: String
│   ├── customFields: [Property]
│   └── registrationConditions: RegistrationConditions
│       ├── memberConditions: [PlayerCondition]
│       └── teamConditions: [TeamCondition]
├── teams: [TournamentTeam]
│   ├── id: ID!
│   ├── name: String!
│   ├── status: TournamentTeamStatus!
│   ├── customFields: [CustomFieldValue]
│   └── members: [TournamentTeamMemberDto]
│       └── playerProfileId: ID!
└── steps: [TournamentStep]  (via tournamentSteps query)
    ├── id: ID!
    ├── name: String!
    ├── description: String
    ├── type: StepType!
    ├── status: StepStatus!
    ├── order: Int!
    └── configuration: StepConfiguration
        └── groups: [GroupConfiguration]
            ├── name: String!
            ├── description: String
            └── rounds: [RoundConfiguration]
                ├── name: String!
                ├── order: Int!
                └── game: GameConfiguration
                    ├── teamsCount: Int
                    ├── wonMatchCountToWinGame: Int!
                    ├── useMatchScoresAsGameScore: Boolean
                    └── matchConfiguration: MatchConfiguration
                        ├── scoreFormula: String
                        └── variables: [MatchVariable]

Generated Shape (after generation):
tournamentStepGeneratedShape: [GeneratedGroup]
├── id: ID!
├── name: String!
└── rounds: [GeneratedRound]
    ├── id: ID!
    ├── name: String!
    ├── order: Int!
    └── games: [GeneratedGame]
        ├── id: ID!
        ├── order: Int!
        ├── status: TournamentGameStatus
        ├── winningGameId: ID  (next game for winner)
        ├── losingGameId: ID   (next game for loser)
        ├── configuration: { wonMatchCountToWinGame: Int }
        └── matches: [GeneratedMatch]
            ├── id: ID!
            ├── order: Int!
            └── status: MatchScoreStatus`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        <Divider
          my="xl"
          label={
            <Text size="lg" fw={700} c="cyan">
              Rule Engine
            </Text>
          }
          labelPosition="center"
        />

        {/* ===================== RULE ENGINE OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconGavel size={18} />}
            title="Rule Engine"
            id="rule-engine"
          />
          <Text mb="md">
            The Rule Engine is an event-driven system that governs tournament
            progression. It manages scoring, team advancement, elimination,
            bracket generation, and cross-step data flow through declarative
            rules, an expression language, and configurable presets.
          </Text>
          <Text mb="md">
            Each tournament step can have one <Code>StepRuleSet</Code> which
            contains:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <Text>
                <Text span fw={600}>
                  Scoring Rule Set
                </Text>{" "}
                &mdash; How scores are calculated and teams ranked
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Text span fw={600}>
                  Advancement Rules
                </Text>{" "}
                &mdash; When and how teams advance or get eliminated
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Text span fw={600}>
                  Structure Template
                </Text>{" "}
                &mdash; The bracket / group / round / game layout
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Text span fw={600}>
                  Cross-Step Rules
                </Text>{" "}
                &mdash; Data imports from other tournament steps
              </Text>
            </List.Item>
          </List>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`StepRuleSet
  +-- ScoringRuleSet (aggregation, formula, point values, tiebreakers)
  +-- AdvancementRules[] (trigger, conditions, actions, priority)
  +-- StructureTemplate
  |     +-- GroupTemplates[] (e.g. "Upper Bracket", "Lower Bracket")
  |           +-- RoundTemplates[] (e.g. "Quarterfinals", "Semifinals")
  |                 +-- GameTemplates[] (individual matchups, bestOf, team slots)
  +-- CrossStepRules[] (seed order, qualify, inject score from other steps)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle size={16} />}
            color="cyan"
            variant="light"
          >
            A newly created step has no rule set. Attach one by applying a
            built-in template (<Code>applyBuiltinPreset</Code>) or an
            organization-authored preset script (<Code>applyPresetScript</Code>),
            or by calling <Code>createStepRuleSet</Code> directly. See the{" "}
            <Anchor href="#builtin-templates">Built-in Templates</Anchor>{" "}
            section for the shipped catalogue.
          </Alert>
        </div>

        {/* ===================== PRESETS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTemplate size={18} />}
            title="Presets"
            id="presets"
          />
          <Text mb="md">
            A <strong>preset</strong> is a parameterised Lua script that
            produces a complete rule set (scoring, tiebreakers, advancement
            rules, structure, and cross-step wiring) for a tournament step.
            Applying a preset runs its script inside the sandbox and persists
            the resulting rule set atomically &mdash; replacing any existing
            one on the target step.
          </Text>
          <Text mb="md">
            There are two kinds of presets:
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <strong>Built-in templates</strong> &mdash; shipped with the
              platform, read-only, identified by a stable slug (e.g.{" "}
              <Code>single-elim</Code>, <Code>swiss</Code>,{" "}
              <Code>double-elim</Code>). Apply with{" "}
              <Code>applyBuiltinPreset</Code> or duplicate with{" "}
              <Code>duplicateBuiltinPreset</Code> to customise. See{" "}
              <Anchor href="#builtin-templates">Built-in Templates</Anchor>.
            </List.Item>
            <List.Item>
              <strong>Preset scripts</strong> &mdash; organization-authored,
              editable, persisted as <Code>PresetScript</Code> rows. Apply with{" "}
              <Code>applyPresetScript</Code>. See{" "}
              <Anchor href="#lua-preset-scripts">Lua Preset Scripts</Anchor>.
            </List.Item>
          </List>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light">
            Both kinds of preset run through the same sandboxed Lua runtime,
            use the same <Code>PRESET</Code> context API, and produce the same
            effect types. The only differences are authorship (platform vs.
            organization) and editability (read-only vs. mutable).
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Apply is replace, not merge
          </Title>
          <Text mb="md">
            Applying any preset to a step &mdash; built-in or
            organization-authored, same preset or different &mdash; is a full
            replacement. In the same transaction, the engine wipes every
            existing artefact on the target step before persisting the new
            preset&apos;s output:
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              All <strong>advancement rules</strong> (including their
              actions).
            </List.Item>
            <List.Item>
              All <strong>cross-step rules</strong>.
            </List.Item>
            <List.Item>
              The <strong>structure template</strong> with every{" "}
              <Code>GroupTemplate</Code> / <Code>RoundTemplate</Code> /{" "}
              <Code>GameTemplate</Code> under it.
            </List.Item>
            <List.Item>
              The <strong>scoring rule set</strong> and every{" "}
              <Code>Tiebreaker</Code> it owns.
            </List.Item>
          </List>
          <Alert icon={<IconInfoCircle />} color="yellow" variant="light">
            <Text size="sm">
              Re-applying the same preset no longer accumulates rules. Every
              apply produces the exact rule set the preset script describes.
              The wipe-and-replace runs inside a rollback-safe transaction:
              if validation or script execution fails, the pre-existing rule
              set is preserved intact &mdash; partial applies are not
              possible.
            </Text>
          </Alert>
        </div>

        {/* ===================== LUA PRESET SCRIPTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Lua Preset Scripts"
            id="lua-preset-scripts"
          />
          <Text mb="md">
            <strong>Lua preset scripts</strong> are organization-authored Lua
            programs that build a complete rule set for a tournament step.
            You can publish your own tournament structure, scoring logic,
            advancement rules, and cross-step wiring as a single parameterised
            script, then apply it to any step with a one-line mutation.
          </Text>

          <Title order={4} mb="xs">
            Built-in templates vs. custom preset scripts
          </Title>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text span fw={600}>
                Built-in templates
              </Text>{" "}
              &mdash; platform-authored, read-only, shared across all
              organizations, versioned alongside the API. The catalogue covers
              22 formats across 6{" "}
              <Code>BuiltinPresetCategory</Code> families &mdash; see{" "}
              <Anchor href="#builtin-templates">Built-in Templates</Anchor>.
              Applied with <Code>applyBuiltinPreset</Code>; duplicate with{" "}
              <Code>duplicateBuiltinPreset</Code> to get an editable copy.
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                Custom preset scripts
              </Text>{" "}
              &mdash; authored by organization users, scoped to an
              organization, and stored in the database. They run in the same
              sandboxed runtime as built-in templates and receive identical
              access to the <Code>PRESET</Code> context API (
              <Code>create_group</Code>, <Code>create_round</Code>,{" "}
              <Code>create_game</Code>, <Code>link</Code>,{" "}
              <Code>set_scoring</Code>, <Code>add_tiebreaker</Code>,{" "}
              <Code>add_rule</Code>, <Code>add_cross_step_rule</Code>). See the{" "}
              <Anchor component={Link} to="/tournaments/docs/lua-reference">
                Lua Reference
              </Anchor>{" "}
              for the full surface.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Managing preset scripts
          </Title>
          <Text size="sm" mb="md">
            Create, edit, and publish preset scripts from{" "}
            <Anchor component={Link} to="/tournaments/preset-scripts">
              Tournaments &rsaquo; Preset Scripts
            </Anchor>
            . Each script carries a name, a description, the Lua source, and a
            typed parameter schema. Drafts can be validated against the{" "}
            <Code>PRESET</Code> sandbox context before publishing; only
            published scripts can be applied to a step.
          </Text>

          <Title order={4} mb="xs">
            Applying a preset script
          </Title>
          <Text size="sm" mb="xs">
            Call the <Code>applyPresetScript</Code> mutation with the script
            ID, the target step, and a JSON-encoded <Code>parameters</Code>{" "}
            object. The script runs once inside a rollback-safe transaction;
            if validation or execution fails, no partial rule set is written.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`mutation ApplyPresetScript(
  $presetScriptId: UUID!,
  $tournamentStepId: UUID!,
  $parameters: String  # JSON object of scalars, keyed by parameter name
) {
  applyPresetScript(
    presetScriptId: $presetScriptId,
    tournamentStepId: $tournamentStepId,
    parameters: $parameters
  ) {
    id
    version
  }
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Parameter declarations
          </Title>
          <Text size="sm" mb="xs">
            Each preset script declares a list of typed parameters. The values
            passed to <Code>applyPresetScript</Code> are validated against
            this schema before the script runs, and the console renders them
            as input controls (number / checkbox / text) in the apply
            dialog. Supported <Code>PresetParameterType</Code> values:
          </Text>
          <List size="sm" mb="md">
            <List.Item>
              <Code>INT</Code> &mdash; integer; surfaces as a number input
              with step 1.
            </List.Item>
            <List.Item>
              <Code>FLOAT</Code> &mdash; floating-point number; surfaces as a
              number input.
            </List.Item>
            <List.Item>
              <Code>STRING</Code> &mdash; text; surfaces as a text input.
            </List.Item>
            <List.Item>
              <Code>BOOLEAN</Code> &mdash; true / false; surfaces as a
              checkbox.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Script limits
          </Title>
          <Text size="sm" mb="xs">
            Preset scripts run inside the same sandboxed runtime as every
            other Lua script on the platform. Limits are enforced at both
            static analysis and runtime:
          </Text>
          <List size="sm" mb="md">
            <List.Item>
              Script size and execution time are bounded. Scripts that exceed
              a limit abort with a structured <Code>ScriptErrorCode</Code>.
            </List.Item>
            <List.Item>
              Dynamic code loading and host-escape built-ins are unavailable:{" "}
              <Code>io</Code>, <Code>os</Code>, <Code>require</Code>,{" "}
              <Code>dofile</Code>, <Code>loadfile</Code>, <Code>load</Code>,{" "}
              <Code>loadstring</Code>, <Code>package</Code>, <Code>debug</Code>
              .
            </List.Item>
            <List.Item>
              Error-catching is not supported: <Code>pcall</Code> and{" "}
              <Code>xpcall</Code> are unavailable &mdash; failures bubble up
              as structured <Code>ScriptErrorCode</Code> values.
            </List.Item>
          </List>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light">
            The full list of available globals and helper functions per
            context (<Code>PRESET</Code>, <Code>ACTION</Code>,{" "}
            <Code>CONDITION</Code>, <Code>FORMULA</Code>, etc.), along with the
            unavailable built-ins, is documented in the{" "}
            <Anchor component={Link} to="/tournaments/docs/lua-reference">
              Lua Reference
            </Anchor>
            .
          </Alert>
        </div>

        {/* ===================== BUILT-IN TEMPLATES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTemplate size={18} />}
            title="Built-in Templates"
            id="builtin-templates"
          />
          <Text mb="md">
            The platform ships a library of ready-to-use tournament-format
            templates grouped by family. Apply one to a step in a single click
            from the console&apos;s preset picker, or duplicate it into an
            editable preset script to customize it.
          </Text>
          <Text mb="md">
            Built-in templates are read-only and shared across all
            organizations. Each template is identified by a stable slug (e.g.{" "}
            <Code>single-elim</Code>, <Code>swiss</Code>) that you pass as the{" "}
            <Code>builtinPresetId</Code> argument to the apply / duplicate
            mutations.
          </Text>

          <Title order={4} mb="xs">
            List &amp; filter
          </Title>
          <Text size="sm" mb="xs">
            Use <Code>builtinPresets</Code> to browse the catalogue, optionally
            narrowing to a specific format family with the{" "}
            <Code>category</Code> argument.
          </Text>
          <GqlBlock>
            {`query {
  builtinPresets(category: SWISS) {
    id
    displayName
    description
    parameters {
      name
      type
      required
      defaultValue
      description
    }
  }
}`}
          </GqlBlock>

          <Title order={4} mt="md" mb="xs">
            Inspect a single template
          </Title>
          <Text size="sm" mb="xs">
            Use <Code>builtinPreset(id)</Code> to fetch the full Lua{" "}
            <Code>source</Code> alongside metadata. This is what powers
            &ldquo;Start from a template&rdquo; in the console preset script
            form.
          </Text>
          <GqlBlock>
            {`query {
  builtinPreset(id: "single-elim") {
    id
    displayName
    description
    category
    source
    parameters {
      name
      type
      required
      defaultValue
      description
    }
  }
}`}
          </GqlBlock>

          <Title order={4} mt="md" mb="xs">
            Apply directly to a step
          </Title>
          <Text size="sm" mb="xs">
            Runs the template inside the Lua sandbox and atomically persists the
            generated rule set on the target step. Existing rules on the step
            are replaced.
          </Text>
          <GqlBlock>
            {`mutation {
  applyBuiltinPreset(
    builtinPresetId: "single-elim"
    tournamentStepId: "<step-id>"
    parameters: "{\\"team_count\\":8,\\"best_of\\":1}"
  ) {
    id
    version
  }
}`}
          </GqlBlock>

          <Title order={4} mt="md" mb="xs">
            Duplicate into your organization
          </Title>
          <Text size="sm" mb="xs">
            Use this when you need to customize a template. The duplicate is a
            normal <Code>PresetScript</Code> &mdash; editable, deletable, and
            versioned per organization. If <Code>name</Code> is omitted, the
            duplicate falls back to the template&apos;s display name (with{" "}
            <Code>(copy)</Code> appended on collision).
          </Text>
          <GqlBlock>
            {`mutation {
  duplicateBuiltinPreset(
    builtinPresetId: "swiss"
    name: "My custom Swiss"
  ) {
    id
    name
  }
}`}
          </GqlBlock>

          <Title order={4} mt="md" mb="xs">
            Categories
          </Title>
          <Text size="sm" mb="xs">
            The <Code>BuiltinPresetCategory</Code> enum is used both to group
            templates in the picker and to filter the{" "}
            <Code>builtinPresets</Code> query.
          </Text>
          <List size="sm" mb="md">
            <List.Item>
              <strong>SINGLE_ELIMINATION</strong> &mdash; classic knockout
              brackets and variants (e.g. <Code>single-elim</Code>,{" "}
              <Code>single-elim-consolation</Code>,{" "}
              <Code>random-draw-knockout</Code>,{" "}
              <Code>king-of-the-hill-bo5</Code>).
            </List.Item>
            <List.Item>
              <strong>DOUBLE_ELIMINATION</strong> &mdash; winners + losers
              brackets, with or without true grand finals (e.g.{" "}
              <Code>double-elim</Code>, <Code>double-elim-true-finals</Code>,{" "}
              <Code>triple-elim</Code>).
            </List.Item>
            <List.Item>
              <strong>SWISS</strong> &mdash; round-by-round pairings without
              elimination (e.g. <Code>swiss</Code>,{" "}
              <Code>swiss-accelerated</Code>, <Code>swiss-mcmahon</Code>,{" "}
              <Code>buchholz-groups</Code>).
            </List.Item>
            <List.Item>
              <strong>ROUND_ROBIN</strong> &mdash; everyone plays everyone (e.g.{" "}
              <Code>round-robin</Code>, <Code>round-robin-bo3</Code>,{" "}
              <Code>round-robin-playoffs</Code>, <Code>league-home-away</Code>).
            </List.Item>
            <List.Item>
              <strong>GROUP_STAGE</strong> &mdash; multi-stage formats with
              pools or page playoffs (e.g. <Code>gsl-format</Code>,{" "}
              <Code>page-playoff</Code>, <Code>gauntlet</Code>,{" "}
              <Code>step-ladder</Code>).
            </List.Item>
            <List.Item>
              <strong>OTHER</strong> &mdash; non-bracket formats such as{" "}
              <Code>cumulative-rounds-series</Code>,{" "}
              <Code>battle-royale</Code>, and <Code>points-race</Code>.
            </List.Item>
          </List>

          <Alert icon={<IconInfoCircle />} color="cyan" variant="light">
            Templates run inside the same sandboxed Lua runtime as
            organization-authored preset scripts. Once duplicated, edits follow
            the standard <Code>PresetScript</Code> validation, simulation, and
            apply lifecycle. See{" "}
            <Anchor href="#lua-preset-scripts">Lua Preset Scripts</Anchor> for
            details.
          </Alert>
        </div>

        {/* ===================== RULE SCORING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconScale size={18} />}
            title="Rule Scoring System"
            id="rule-scoring"
          />
          <Text mb="md">
            The scoring rule set determines how team scores are calculated and
            how ties are broken. Each rule set has one scoring configuration.
          </Text>

          <Title order={4} mb="xs">
            Aggregation Modes
          </Title>
          <Table
            className={classes.statusTable}
            striped
            highlightOnHover
            mb="md"
          >
            <thead>
              <tr>
                <th>Mode</th>
                <th>Description</th>
                <th>Typical Use</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>SUM</Code>
                </td>
                <td>Sum of all match scores</td>
                <td>Elimination brackets</td>
              </tr>
              <tr>
                <td>
                  <Code>AVERAGE</Code>
                </td>
                <td>Average of all match scores</td>
                <td>Normalized scoring across unequal game counts</td>
              </tr>
              <tr>
                <td>
                  <Code>POINTS</Code>
                </td>
                <td>Result-based points (Win / Draw / Loss / Forfeit)</td>
                <td>Round robin, Swiss, leagues</td>
              </tr>
              <tr>
                <td>
                  <Code>FORMULA</Code>
                </td>
                <td>Custom expression evaluated per team</td>
                <td>Complex or custom scoring formats</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Tiebreakers
          </Title>
          <Text mb="xs">
            Tiebreakers are evaluated in order (lowest <Code>order</Code> first)
            when teams have identical primary scores. Each produces a numeric
            value sorted by <Code>sortOrder</Code> (ASC or DESC).
          </Text>
          <Table
            className={classes.statusTable}
            striped
            highlightOnHover
            mb="md"
          >
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Sort</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Head-to-Head</td>
                <td>Direct matchup result between tied teams</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Score Difference</td>
                <td>Total scored minus total conceded</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Buchholz</td>
                <td>Sum of all opponents&apos; scores (Swiss)</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Sonnborn-Berger</td>
                <td>Sum of beaten opponents&apos; scores</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Median Buchholz</td>
                <td>Buchholz minus best and worst opponents</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Total Score</td>
                <td>Raw total score</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Wins</td>
                <td>Number of wins</td>
                <td>DESC</td>
              </tr>
              <tr>
                <td>Custom Formula</td>
                <td>Evaluate custom expression</td>
                <td>Configurable</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== ADVANCEMENT RULES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPlaylistAdd size={18} />}
            title="Advancement Rules"
            id="advancement-rules"
          />
          <Text mb="md">
            Advancement rules define when and how teams progress through a
            tournament. Each rule has a trigger event, conditions, and actions.
            Rules are evaluated in priority order (lowest <Code>order</Code>{" "}
            first).
          </Text>

          <Title order={4} mb="xs">
            Trigger Events
          </Title>
          <Table
            className={classes.statusTable}
            striped
            highlightOnHover
            mb="md"
          >
            <thead>
              <tr>
                <th>Event</th>
                <th>When It Fires</th>
                <th>Typical Use</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>MATCH_ENDED</Code>
                </td>
                <td>A match within a game completes</td>
                <td>Score updates</td>
              </tr>
              <tr>
                <td>
                  <Code>GAME_ENDED</Code>
                </td>
                <td>All matches in a game complete</td>
                <td>Bracket advancement / elimination</td>
              </tr>
              <tr>
                <td>
                  <Code>ROUND_ENDED</Code>
                </td>
                <td>All games in a round complete</td>
                <td>Swiss round generation</td>
              </tr>
              <tr>
                <td>
                  <Code>GROUP_ENDED</Code>
                </td>
                <td>All rounds in a group complete</td>
                <td>Final standings, cross-bracket advancement</td>
              </tr>
              <tr>
                <td>
                  <Code>STEP_ENDED</Code>
                </td>
                <td>All groups in a step complete</td>
                <td>Cross-step advancement</td>
              </tr>
              <tr>
                <td>
                  <Code>MANUAL</Code>
                </td>
                <td>Organizer triggers manually</td>
                <td>Override / correction</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Action Types
          </Title>
          <Text size="sm" mb="xs">
            The <Code>RuleActionType</Code> enum has four values. The first
            three are built-in effects with a typed payload; the fourth
            (<Code>EXECUTE_LUA</Code>) runs a Lua action script that can
            produce any combination of sandbox effects (advancing a team,
            setting metadata, ending an entity, generating a round, etc.).
          </Text>
          <Accordion variant="separated" mb="md">
            <Accordion.Item value="advance">
              <Accordion.Control>
                <Group>
                  <Badge color="cyan" variant="light">
                    ADVANCE
                  </Badge>
                  <Text size="sm">
                    Move a team to a specific game, group, or step
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Targets a specific game / group / step by ID or by relative
                  reference (<Code>NEXT</Code>, <Code>PREVIOUS</Code>,{" "}
                  <Code>CURRENT</Code>, or index-based). Rank mapping
                  (<Code>BY_RANK</Code>, <Code>SEED_OPTIMIZED</Code>,{" "}
                  <Code>RANDOM</Code>) controls placement when the target is a
                  group.
                </Text>
                <GqlBlock>
                  {`{
  "type": "ADVANCE",
  "target": {
    "kind": "GAME",
    "relative": { "groupIndex": 0, "roundIndex": 1, "gameIndex": 0, "slot": 0 }
  }
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="eliminate">
              <Accordion.Control>
                <Group>
                  <Badge color="red" variant="light">
                    ELIMINATE
                  </Badge>
                  <Text size="sm">Mark a team as eliminated</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>{`{ "type": "ELIMINATE" }`}</GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="end">
              <Accordion.Control>
                <Group>
                  <Badge color="gray" variant="light">
                    END
                  </Badge>
                  <Text size="sm">
                    Mark a game, round, group, or step as completed
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  A single <Code>END</Code> action chooses its target entity
                  via its <Code>scope</Code> field. Without a scope, it ends
                  the current entity in which the rule fired.
                </Text>
                <GqlBlock>
                  {`{ "type": "END", "scope": { "kind": "GROUP" } }

{ "type": "END", "scope": { "kind": "GROUP", "relative": { "groupIndex": 0 } } }`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="execute-lua">
              <Accordion.Control>
                <Group>
                  <Badge color="teal" variant="light">
                    EXECUTE_LUA
                  </Badge>
                  <Text size="sm">
                    Run a Lua action script to produce arbitrary effects
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  The script runs in the <Code>ACTION</Code> sandbox context
                  and can call effect mutators like{" "}
                  <Code>advance(team, target)</Code>,{" "}
                  <Code>eliminate(team)</Code>,{" "}
                  <Code>end_game(game)</Code>, <Code>end_round(round)</Code>,{" "}
                  <Code>end_group(group)</Code>,{" "}
                  <Code>set_winner(team, step)</Code>,{" "}
                  <Code>set_eliminated(team, step)</Code>,{" "}
                  <Code>set_status(team, status)</Code>,{" "}
                  <Code>set_metadata(entity, key, value)</Code>,{" "}
                  <Code>set_custom_field(entity, key, value)</Code>, and{" "}
                  <Code>register_team(team, target)</Code>. Any condition that
                  needs logic richer than the built-in <Code>ADVANCE</Code> /{" "}
                  <Code>ELIMINATE</Code> / <Code>END</Code> effect payloads is
                  expressed here. See the{" "}
                  <Anchor component={Link} to="/tournaments/docs/lua-reference">
                    Lua Reference
                  </Anchor>{" "}
                  for the full signature of each mutator.
                </Text>
                <GqlBlock>
                  {`{
  "type": "EXECUTE_LUA",
  "script": [[
    -- Advance along the WIN link if wired, otherwise mark step winner.
    if game.win_link_target ~= nil then
      advance(team, by_id(game.win_link_target))
    else
      set_winner(team, step)
    end
  ]]
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Conditions
          </Title>
          <Text mb="xs">
            Each rule has a single <Code>condition</Code> field containing a{" "}
            <strong>Lua expression</strong> that must evaluate to a truthy value
            for the rule to fire. The expression reads from the current scope
            (team, game, match, group, round, step) and may call any built-in
            ranking / scoring function exposed by the Lua sandbox (e.g.{" "}
            <Code>game.result == &quot;WIN&quot;</Code>,{" "}
            <Code>rank(team.id, &#123; by = &quot;score&quot; &#125;) &lt;= 4</Code>
            ). Multiple conditions are combined directly in Lua using{" "}
            <Code>and</Code> / <Code>or</Code> / <Code>not</Code> &mdash; there
            is no separate <Code>conditionOperator</Code> any more.
          </Text>

          <Text size="sm" mb="xs">
            Example condition &mdash; &quot;Team won the game&quot;:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>{`game.result == "WIN"`}</GqlBlock>
          </Card>

          <Text size="sm" mt="sm" mb="xs">
            Example compound condition &mdash; &quot;Winner of upper bracket
            final&quot;:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`game.result == "WIN" and group.index == 0 and game.order == 3`}
            </GqlBlock>
          </Card>
        </div>

        {/* ===================== EXPRESSION LANGUAGE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconMathFunction size={18} />}
            title="Expression Language"
            id="expressions"
          />
          <Text mb="md">
            The rule engine evaluates scoring formulas, tiebreakers, and
            conditions as <strong>Lua expressions</strong>. Scripts run inside
            a sandboxed runtime with bounded execution and no access to{" "}
            <Code>io</Code>, <Code>os</Code>, <Code>require</Code>,{" "}
            <Code>pcall</Code> or other escape hatches. The API persists and
            returns these as plain Lua source strings (e.g.{" "}
            <Code>team.wins * 3 + team.draws</Code>). See the{" "}
            <Anchor component={Link} to="/tournaments/docs/lua-reference">
              Lua Reference
            </Anchor>{" "}
            for the full list of available globals, helper functions, and
            script limits.
          </Text>

          <Title order={4} mb="xs">
            Variables
          </Title>
          <Accordion variant="separated" mb="md">
            <Accordion.Item value="team-vars">
              <Accordion.Control>
                Team Context (always available)
              </Accordion.Control>
              <Accordion.Panel>
                <Table className={classes.statusTable} striped>
                  <thead>
                    <tr>
                      <th>Variable</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["team.id", "string", "Team identifier"],
                      ["team.seed", "number", "Seeding position"],
                      ["team.wins", "number", "Total wins"],
                      ["team.losses", "number", "Total losses"],
                      ["team.draws", "number", "Total draws"],
                      ["team.forfeits", "number", "Total forfeits"],
                      ["team.score", "number", "Calculated score"],
                      ["team.matchesPlayed", "number", "Matches played"],
                      ["team.gamesPlayed", "number", "Games played"],
                      ["team.properties.<key>", "any", "Custom properties"],
                    ].map(([v, t, d]) => (
                      <tr key={v}>
                        <td>
                          <Code>{v}</Code>
                        </td>
                        <td>{t}</td>
                        <td>{d}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="game-vars">
              <Accordion.Control>
                Game &amp; Match Context (scope-dependent)
              </Accordion.Control>
              <Accordion.Panel>
                <Table className={classes.statusTable} striped>
                  <thead>
                    <tr>
                      <th>Variable</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["game.id", "string", "Game identifier"],
                      ["game.order", "number", "Game order in round"],
                      ["game.result", "string", "WIN / LOSS / DRAW / PENDING"],
                      ["game.score", "number", "Game score"],
                      ["match.id", "string", "Match identifier"],
                      ["match.score", "number", "Match score"],
                      [
                        "match.result",
                        "string",
                        "WIN / LOSS / DRAW / FORFEIT / PENDING",
                      ],
                      [
                        "opponents",
                        "array<OpponentContext>",
                        "Sequence of every other team in the event (1..N), excluding the current team. Empty for solo events, 1 entry for head-to-head, N−1 for battle royale / free-for-all. Use #opponents for the count and ipairs(opponents) to iterate.",
                      ],
                      [
                        "opponents[i].id",
                        "string",
                        "Opponent team ID. Always present. Feed to by_id(...) to look up the full team record.",
                      ],
                      [
                        "opponents[i].score",
                        "number?",
                        "Opponent's score for the current event (optional — present when the event carries scores).",
                      ],
                      [
                        "opponents[i].result",
                        "string?",
                        "Opponent's match outcome for the current event: WIN / LOSS / DRAW / FORFEIT (optional).",
                      ],
                      ["group.index", "number", "Group index (0-based)"],
                      ["round.index", "number", "Round index (0-based)"],
                    ].map(([v, t, d]) => (
                      <tr key={v}>
                        <td>
                          <Code>{v}</Code>
                        </td>
                        <td>{t}</td>
                        <td>{d}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Operators
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
            <Card className={classes.card} p="sm" radius="md">
              <Text size="sm" fw={600} mb={4}>
                Arithmetic
              </Text>
              <Text size="xs" c="dimmed">
                <Code>+</Code> <Code>-</Code> <Code>*</Code> <Code>/</Code>{" "}
                <Code>%</Code> <Code>^</Code> <Code>//</Code>
              </Text>
            </Card>
            <Card className={classes.card} p="sm" radius="md">
              <Text size="sm" fw={600} mb={4}>
                Comparison
              </Text>
              <Text size="xs" c="dimmed">
                <Code>==</Code> <Code>~=</Code> <Code>&gt;</Code>{" "}
                <Code>&gt;=</Code> <Code>&lt;</Code> <Code>&lt;=</Code>
              </Text>
            </Card>
            <Card className={classes.card} p="sm" radius="md">
              <Text size="sm" fw={600} mb={4}>
                Logical &amp; Control
              </Text>
              <Text size="xs" c="dimmed">
                <Code>and</Code> <Code>or</Code> <Code>not</Code>{" "}
                <Code>if ... then ... else ... end</Code>
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Built-in Helper Functions
          </Title>
          <Text size="sm" mb="xs">
            Available in every context except <Code>GENERATION</Code>. The{" "}
            <Anchor component={Link} to="/tournaments/docs/lua-reference">
              Lua Reference
            </Anchor>{" "}
            lists every helper with its signature; the tables below summarise
            the most common ones.
          </Text>
          <Accordion variant="separated" mb="md">
            <Accordion.Item value="fn-ranking">
              <Accordion.Control>Ranking &amp; Scoring</Accordion.Control>
              <Accordion.Panel>
                <Table className={classes.statusTable} striped>
                  <thead>
                    <tr>
                      <th>Function</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "rank(team, opts?)",
                        "Team's rank (1-based). opts may include by and scope.",
                      ],
                      [
                        "step_rank(team, step)",
                        "Team rank from another step",
                      ],
                      [
                        "step_score(team, step)",
                        "Team score from another step",
                      ],
                      [
                        "head_to_head(teamA, teamB, opts?)",
                        "Direct matchup comparison",
                      ],
                    ].map(([fn, desc]) => (
                      <tr key={fn}>
                        <td>
                          <Code>{fn}</Code>
                        </td>
                        <td>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="fn-swiss">
              <Accordion.Control>Swiss Tiebreakers</Accordion.Control>
              <Accordion.Panel>
                <Table className={classes.statusTable} striped>
                  <thead>
                    <tr>
                      <th>Function</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["buchholz(team)", "Sum of all opponents' scores"],
                      [
                        "sonnborn_berger(team)",
                        "Sum of beaten opponents' scores",
                      ],
                      [
                        "median_buchholz(team)",
                        "Buchholz minus best and worst opponents",
                      ],
                      [
                        "cumulative(team)",
                        "Running score accumulation over rounds",
                      ],
                    ].map(([fn, desc]) => (
                      <tr key={fn}>
                        <td>
                          <Code>{fn}</Code>
                        </td>
                        <td>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="fn-aggregate">
              <Accordion.Control>
                Aggregation, Metadata &amp; Utility
              </Accordion.Control>
              <Accordion.Panel>
                <Table className={classes.statusTable} striped>
                  <thead>
                    <tr>
                      <th>Function</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "sum(collection, mapper?)",
                        "Sum of values (optionally via mapper predicate)",
                      ],
                      ["count(collection, predicate?)", "Count of items"],
                      ["avg(collection, mapper?)", "Arithmetic mean"],
                      ["min(collection, mapper?)", "Minimum value"],
                      ["max(collection, mapper?)", "Maximum value"],
                      [
                        "metadata(entity, key)",
                        "Read a metadata entry on an entity. Returns Lua nil (not a JS-null sentinel) when the key has no value, so the `metadata(t, \"k\") or default` idiom works safely.",
                      ],
                      [
                        "custom_field(entity, key)",
                        "Read custom-field value on an entity",
                      ],
                      [
                        "by_id(id)",
                        "Resolve an entity reference from its UUID",
                      ],
                      ["coalesce(v1, v2, ...)", "First non-nil value"],
                      ["clamp(value, min, max)", "Constrain to range"],
                    ].map(([fn, desc]) => (
                      <tr key={fn}>
                        <td>
                          <Code>{fn}</Code>
                        </td>
                        <td>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Examples
          </Title>

          <Text size="sm" fw={600} mb={4}>
            Scoring formula (<Code>FORMULA</Code> context, returns a number)
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`-- 3 points per win, 1 per draw
return 3 * count(team.matches, function(m) return m.result == "WIN" end)
     + 1 * count(team.matches, function(m) return m.result == "DRAW" end)`}
            </GqlBlock>
          </Card>

          <Text size="sm" fw={600} mb={4}>
            Advancement condition (<Code>CONDITION</Code> context, returns a
            boolean)
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`-- Team won the best-of-N majority
local needed = math.ceil(game.best_of / 2)
return team.matches_won >= needed`}
            </GqlBlock>
          </Card>

          <Text size="sm" fw={600} mb={4}>
            Iterating over <Code>opponents</Code>
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`-- opponents is a 1-indexed Lua sequence scoped to the current event.
-- Shape: { id: string, score?: number, result?: "WIN"|"LOSS"|"DRAW"|"FORFEIT" }
-- Size scales with event arity:
--   solo events         -> #opponents == 0
--   head-to-head        -> #opponents == 1
--   battle royale / FFA -> #opponents == N - 1

-- Guard for solo events before indexing
if #opponents == 0 then return false end

-- Head-to-head shortcut
opponents[1].id

-- Sum of every opponent's score in this event (works for N ≥ 0)
local total = 0
for _, opp in ipairs(opponents) do
  total = total + (opp.score or 0)
end

-- Count opponents the current team beat in this event
local beat = 0
for _, opp in ipairs(opponents) do
  if opp.result == "LOSS" then beat = beat + 1 end
end`}
            </GqlBlock>
          </Card>

          <Text size="sm" fw={600} mb={4}>
            Tiebreaker formula (<Code>FORMULA</Code> context)
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`-- Buchholz: sum of all opponents' total scores
return buchholz(team)`}
            </GqlBlock>
          </Card>

          <Text size="sm" fw={600} mb={4}>
            Cross-step expression (<Code>CROSS_STEP</Code> context)
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`-- Carry over 50% of the previous step's score
return step_score(team, source_step) * 0.5`}
            </GqlBlock>
          </Card>
        </div>

        {/* ===================== STRUCTURE TEMPLATES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTournament size={18} />}
            title="Structure Templates"
            id="structure-templates"
          />
          <Text mb="md">
            Structure templates define the bracket / group layout. The hierarchy
            is:{" "}
            <Code>
              StructureTemplate &rarr; GroupTemplate[] &rarr; RoundTemplate[]
              &rarr; GameTemplate[]
            </Code>
            .
          </Text>

          <Title order={4} mb="xs">
            Building structure from Lua
          </Title>
          <Text mb="xs">
            Structure is produced imperatively by a preset or generation script
            via the <Code>create_group</Code>, <Code>create_round</Code>,{" "}
            <Code>create_game</Code>, and <Code>link</Code> builders. A group
            that needs runtime-generated rounds (e.g. Swiss, dynamic pools)
            sets a <Code>generation_script</Code> on the group template; the
            engine runs it in the <Code>GENERATION</Code> context when the
            group is materialised. See the{" "}
            <Anchor component={Link} to="/tournaments/docs/lua-reference">
              Lua Reference
            </Anchor>{" "}
            for the full builder surface and the list of injected globals
            (<Code>team_count</Code>, <Code>best_of</Code>, <Code>group</Code>).
          </Text>

          <Text size="sm" mb="xs">
            Example &mdash; 8-team single elimination bracket built from a
            preset script:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`local bracket = create_group({ name = "Bracket", team_slots = 8 })
for r = 1, 3 do
  local round = create_round(bracket, { name = "Round " .. r, order = r })
  for g = 1, 8 // (2 ^ r) do
    create_game(round, { order = g, team_slots = 2, best_of = best_of })
  end
end
-- link(sourceGame, targetGame, "WIN") wires winners between rounds.`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="lg" mb="xs">
            Chain-layout seeding
          </Title>
          <Text size="sm" mb="xs">
            When the generated structure is a <strong>chain layout</strong>{" "}
            &mdash; exactly 1 game per round, 2 team slots per game, each
            game <Code>WIN</Code>-linked forward to the next round&apos;s
            game &mdash; the seeder detects the topology and spreads one seed
            into each round&apos;s game, rather than packing everything into
            round 1. This is how the <Code>gauntlet</Code>,{" "}
            <Code>step-ladder</Code>, and <Code>king-of-the-hill</Code>{" "}
            built-in templates get their canonical bracket. Preset authors do
            not need any special seeding code: as long as the layout matches
            the shape above, chain seeding just works.
          </Text>
        </div>

        {/* ===================== CROSS-STEP RULES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTimeline size={18} />}
            title="Cross-Step Rules"
            id="cross-step-rules"
          />
          <Text mb="md">
            Cross-step rules import data from other tournament steps, enabling
            multi-stage tournaments (e.g. qualifiers &rarr; playoffs &rarr;
            finals). Existing rules are displayed in a table with columns:{" "}
            <Text span fw={600}>
              Source
            </Text>
            ,{" "}
            <Text span fw={600}>
              Data Source
            </Text>
            ,{" "}
            <Text span fw={600}>
              Type
            </Text>{" "}
            (badge),{" "}
            <Text span fw={600}>
              Expression
            </Text>
            , and{" "}
            <Text span fw={600}>
              Actions
            </Text>{" "}
            (delete).
          </Text>

          <Title order={4} mb="xs">
            Usage Types
          </Title>
          <Table
            className={classes.statusTable}
            striped
            highlightOnHover
            mb="md"
          >
            <thead>
              <tr>
                <th>Usage Type</th>
                <th>Description</th>
                <th>Extra Field</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>SEED_ORDER</Code>
                </td>
                <td>Use rankings from source step to seed teams</td>
                <td>None</td>
              </tr>
              <tr>
                <td>
                  <Code>QUALIFY</Code>
                </td>
                <td>Filter teams by a condition expression</td>
                <td>Condition (Lua expression)</td>
              </tr>
              <tr>
                <td>
                  <Code>INJECT_SCORE</Code>
                </td>
                <td>Add bonus score via a formula expression</td>
                <td>Formula (Lua expression)</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Add Cross-Step Rule (modal)
          </Title>
          <Text size="sm" mb="xs">
            Click{" "}
            <Text span fw={600}>
              Add Cross-Step Rule
            </Text>{" "}
            to open the creation modal with the following fields:
          </Text>
          <List size="sm" mb="md">
            <List.Item>
              <Text span fw={600}>
                Source Step
              </Text>{" "}
              (optional) &mdash; searchable select listing all other steps in
              the tournament by name and order. Use this to reference a specific
              step by ID.
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                Source Relative
              </Text>{" "}
              (optional) &mdash; select with options: <Code>Previous Step</Code>
              , <Code>Next Step</Code>, <Code>Current Step</Code>. Use this
              instead of Source Step for position-based references.
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                Data Source
              </Text>{" "}
              &mdash; what data to pull: <Code>Rankings</Code>,{" "}
              <Code>Scores</Code>, or <Code>Teams</Code>.
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                Usage Type
              </Text>{" "}
              &mdash; how the imported data is used (see table above).
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                Condition
              </Text>{" "}
              (QUALIFY only) &mdash; Lua expression that teams must satisfy,
              e.g. <Code>{`step_rank(team.id, "PREVIOUS") <= 8`}</Code>
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                Formula
              </Text>{" "}
              (INJECT_SCORE only) &mdash; Lua expression to compute the injected
              score, e.g. <Code>{`step_score(team.id, "PREVIOUS") * 0.5`}</Code>
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Examples
          </Title>
          <Text size="sm" mb="xs">
            Qualify top 8 teams from previous step:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`Source Relative:  Previous Step
Data Source:      Rankings
Usage Type:       Qualify
Condition (Lua):  return step_rank(team, source_step) <= 8`}
            </GqlBlock>
          </Card>

          <Text size="sm" mb="xs">
            Carry over 50% of previous step score:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`Source Relative:  Previous Step
Data Source:      Scores
Usage Type:       Inject Score
Formula (Lua):    return step_score(team, source_step) * 0.5`}
            </GqlBlock>
          </Card>

          <Text size="sm" mb="xs">
            Seed playoffs based on a specific step&apos;s rankings:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Source Step:       Group Stage (#1)
Data Source:       Rankings
Usage Type:       Seed Order`}
            </GqlBlock>
          </Card>
        </div>

        {/* ===================== RULE ENGINE API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Rule Engine API"
            id="rule-engine-api"
          />
          <Text mb="md">
            GraphQL queries and mutations for the rule engine. All mutations are
            authenticated and require the <Code>organization-id</Code> header.
          </Text>

          <Title order={4} mb="xs">
            Queries
          </Title>
          <Accordion variant="separated" mb="md">
            <Accordion.Item value="q-stepruleset">
              <Accordion.Control>
                <Group>
                  <Badge color="blue" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Code>stepRuleSet(tournamentStepId)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Retrieve the full rule set for a tournament step. Returns{" "}
                  <Code>null</Code> if no rule set exists. Formula, tiebreaker,
                  and condition fields are plain Lua source strings (e.g.{" "}
                  <Code>team.wins * 3 + team.draws</Code>,{" "}
                  <Code>game.result == &quot;WIN&quot;</Code>).
                </Text>
                <GqlBlock>
                  {`query StepRuleSet($tournamentStepId: ID!) {
  stepRuleSet(tournamentStepId: $tournamentStepId) {
    id
    version
    scoringRuleSet {
      aggregation
      formula
      winPoints
      lossPoints
      drawPoints
      forfeitPoints
      tiebreakers { order, type, formula, sortOrder }
    }
    advancementRules {
      id, name, order, triggerType,
      condition, actions, autoExecute, enabled
    }
    structureTemplate {
      groupTemplates {
        name, order, teamSlotsExpr, bestOfExpr,
        teamSlotsPerGameExpr, roundCountExpr, generationScript,
        roundTemplates {
          name, order
          gameTemplates {
            order, teamSlots, bestOf,
            onWinTarget, onWinSourceSlot, onWinTargetSlot,
            onLossTarget, onLossSourceSlot, onLossTargetSlot
          }
        }
      }
    }
    crossStepRules { sourceRelative, dataSource, usage }
  }
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="q-validate">
              <Accordion.Control>
                <Group>
                  <Badge color="blue" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Code>validateScript(input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Dry validation of a Lua script against a target{" "}
                  <Code>ScriptContextType</Code>. Runs size, static-analysis,
                  and sandbox checks without persisting. Returns errors (with
                  an error code and line/column) and warnings.
                </Text>
                <GqlBlock>
                  {`query Validate($input: ValidateScriptInput!) {
  validateScript(input: $input) {
    valid
    errors { code, path, message, line, column }
    warnings { code, path, message }
  }
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="q-simulate">
              <Accordion.Control>
                <Group>
                  <Badge color="blue" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Code>simulateScript(input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Dry-run a Lua script with synthetic inputs. Returns the
                  collected sandbox effects (mutations the script would have
                  applied) plus any runtime errors &mdash; nothing is
                  persisted.
                </Text>
                <GqlBlock>
                  {`query Simulate($input: SimulateScriptInput!) {
  simulateScript(input: $input) {
    success
    instructionsUsed
    executionTimeMs
    errors {
      line column code message
      phase originHelper hint
      snippet { lineNumber source isErrorLine }
      stackFrames { phase line helperName description }
    }
    warnings { line column code message }
    effectsSummary { type description }
    partialEffectsSummary { type description }
  }
}`}
                </GqlBlock>
                <Text size="sm" mt="xs">
                  When the script throws, <Code>errors[].phase</Code> tells you
                  whether the error happened in the host-injected prelude, the
                  sandbox bootstrap, or your script.{" "}
                  <Code>errors[].originHelper</Code> names the prelude helper
                  when <Code>phase</Code> is <Code>PRELUDE</Code>.{" "}
                  <Code>errors[].snippet</Code> is a 5-line excerpt around the
                  error line for <Code>USER_SCRIPT</Code> errors.{" "}
                  <Code>errors[].stackFrames</Code> is the full Lua stack with
                  prelude lines mapped to helper names.{" "}
                  <Code>partialEffectsSummary</Code> lists the effects the
                  script had already produced before throwing &mdash; useful
                  for diagnosing rules that fail mid-loop. None of the partial
                  effects are applied; they are diagnostic only.
                </Text>
                <Text size="sm" mt="xs">
                  <strong>Recursive PRESET validation.</strong> When{" "}
                  <Code>context</Code> is <Code>PRESET</Code>, the simulator
                  also runs every inner <Code>add_rule(...)</Code>{" "}
                  <Code>condition</Code> body, every{" "}
                  <Code>EXECUTE_LUA</Code> action body, and every{" "}
                  <Code>add_tiebreaker(...)</Code> formula in their own
                  sandboxes. Failures bubble up into the outer{" "}
                  <Code>errors[]</Code> array with the inner message prefixed
                  for attribution:
                </Text>
                <List size="sm" withPadding spacing={2}>
                  <List.Item>
                    <Code>
                      In rule &apos;&lt;name&gt;&apos; condition: &lt;inner
                      message&gt;
                    </Code>
                  </List.Item>
                  <List.Item>
                    <Code>
                      In rule &apos;&lt;name&gt;&apos; action #&lt;n&gt;:
                      &lt;inner message&gt;
                    </Code>
                  </List.Item>
                  <List.Item>
                    <Code>
                      In tiebreaker &apos;&lt;type&gt;&apos; formula: &lt;inner
                      message&gt;
                    </Code>
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  When the rule has no <Code>name</Code>, the prefix uses{" "}
                  <Code>&apos;(unnamed)&apos;</Code>. The other error fields
                  (<Code>line</Code>, <Code>column</Code>, <Code>phase</Code>,{" "}
                  <Code>originHelper</Code>, <Code>hint</Code>,{" "}
                  <Code>snippet</Code>, <Code>stackFrames</Code>) on a folded
                  error refer to the <em>inner</em> body &mdash; e.g.{" "}
                  <Code>line</Code> is the line inside the action&apos;s Lua
                  body, not inside the outer preset.{" "}
                  <Code>success</Code> is <Code>false</Code> as soon as any
                  inner body errored.{" "}
                  <Code>effectsSummary</Code>,{" "}
                  <Code>partialEffectsSummary</Code>,{" "}
                  <Code>executionTimeMs</Code>, and{" "}
                  <Code>instructionsUsed</Code> still reflect the outer
                  preset run only.
                </Text>
                <Text size="sm" mt="xs">
                  <strong>Structured overrides.</strong> Use{" "}
                  <Code>overrides: SimulationContextOverridesInput</Code> to
                  resize the synthetic fixture, seed per-team metadata, and
                  override preset parameters with strongly-typed values. This
                  replaces the deprecated JSON-string{" "}
                  <Code>parameters</Code> blob and exposes four sub-fields:
                </Text>
                <List size="sm" withPadding spacing={2}>
                  <List.Item>
                    <Code>teamCount: Int</Code> &mdash; number of synthetic
                    teams on the step. Clamped to a multiple of 4 between 4
                    and 64. Default <Code>8</Code>.
                  </List.Item>
                  <List.Item>
                    <Code>bestOf: Int</Code> &mdash; matches per game in the
                    synthetic bracket. Clamped between 1 and 99. Default{" "}
                    <Code>1</Code>.
                  </List.Item>
                  <List.Item>
                    <Code>teams: [SimulationTeamOverrideInput!]</Code> &mdash;
                    per-team overlays applied on top of the synthetic team
                    list. Each entry targets a team by{" "}
                    <Code>position</Code> (1-based) or synthetic{" "}
                    <Code>id</Code> (e.g. <Code>&quot;team-1&quot;</Code>) and
                    can install <Code>metadata</Code>,{" "}
                    <Code>customFields</Code>, <Code>rank</Code>, and{" "}
                    <Code>status</Code>. Use this to seed{" "}
                    <Code>metadata</Code> bags so sort-by-metadata scripts
                    produce meaningful previews instead of all-tied results
                    from empty bags.
                  </List.Item>
                  <List.Item>
                    <Code>
                      presetParameters:
                      [SimulationPresetParameterInput!]
                    </Code>{" "}
                    &mdash; user-defined parameter overrides for{" "}
                    <Code>PRESET</Code>-context simulations. Each entry
                    carries <Code>name</Code>, a discriminator{" "}
                    <Code>type: STRING | NUMBER | BOOLEAN</Code>, and the
                    corresponding <Code>stringValue</Code>,{" "}
                    <Code>numberValue</Code>, or <Code>booleanValue</Code>.
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  All four fields are optional; omit them individually or send
                  <Code>null</Code> to accept the simulator defaults.
                </Text>
                <Text size="sm" mt="xs">
                  Console UI currently exposes <Code>position</Code> and{" "}
                  <Code>metadata</Code> only; <Code>customFields</Code>,{" "}
                  <Code>rank</Code>, <Code>status</Code> are accepted by the
                  API and can be sent via direct GraphQL clients while we
                  ship the corresponding UI controls.
                </Text>
                <Alert color="yellow" mt="xs" icon={<IconAlertCircle size={16} />}>
                  <strong>Deprecated:</strong>{" "}
                  <Code>parameters: String</Code> (JSON-encoded). Violates the
                  no-JSON-in-GraphQL contract; use{" "}
                  <Code>overrides</Code> instead. When both are supplied,{" "}
                  <Code>overrides</Code> wins. The{" "}
                  <Code>parameters</Code> field will be removed in the next
                  major release.
                </Alert>
                <Text size="sm" mt="xs">
                  <strong>Example: seeding metadata for a cumulative-rank
                  preview.</strong> Resize to four synthetic teams and
                  install per-team <Code>lgts_points</Code> values so a
                  sort-by-metadata script produces a deterministic ordering
                  instead of an all-tied bag:
                </Text>
                <GqlBlock>
                  {`overrides: {
  teamCount: 4,
  teams: [
    { position: 1, metadata: [{ key: "lgts_points", value: "10" }] },
    { position: 2, metadata: [{ key: "lgts_points", value: "5" }] },
    { position: 3, metadata: [{ key: "lgts_points", value: "8" }] },
    { position: 4, metadata: [{ key: "lgts_points", value: "1" }] },
  ],
}`}
                </GqlBlock>
                <Text size="sm" mt="xs">
                  <strong>Example.</strong> A minimal preset with one rule
                  whose first action has a syntax error:
                </Text>
                <GqlBlock>
                  {`-- preset script (outer body)
add_rule({
  name = "cumulative-rank",
  condition = "return true",
  actions = {
    {
      type = "EXECUTE_LUA",
      script = "local x = nil\\nreturn x.foo",
    },
  },
})`}
                </GqlBlock>
                <Text size="sm" mt="xs">
                  Calling <Code>simulateScript</Code> with{" "}
                  <Code>context: PRESET</Code> and{" "}
                  <Code>
                    {`overrides: { teamCount: 16, bestOf: 3 }`}
                  </Code>{" "}
                  returns one folded error roughly shaped like:
                </Text>
                <GqlBlock>
                  {`{
  "success": false,
  "errors": [
    {
      "line": 2,
      "column": 12,
      "code": "RUNTIME_ERROR",
      "phase": "USER_SCRIPT",
      "message": "In rule 'cumulative-rank' action #1: attempt to index a nil value (local 'x')",
      "snippet": [ /* 5-line excerpt of the action body */ ],
      "stackFrames": [ /* stack inside the inner action */ ]
    }
  ],
  "effectsSummary": [ /* effects emitted by the OUTER preset only */ ],
  "instructionsUsed": 412,
  "executionTimeMs": 7
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="q-logs">
              <Accordion.Control>
                <Group>
                  <Badge color="blue" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Code>stepRuleExecutionLogs(stepId, pageInfo)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Paginated execution audit trail. Cursor-based pagination.
                </Text>
                <GqlBlock>
                  {`query Logs($stepId: UUID!, $pageInfo: PageInfo!) {
  stepRuleExecutionLogs(stepId: $stepId, pageInfo: $pageInfo) {
    edges {
      node {
        id, advancementRuleId, ruleSetVersion,
        conditionsMet, manualOverride, createdAt,
        triggerEventData, actionsExecuted
      }
    }
    pageInfo { hasNextPage, endCursor }
  }
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="q-presets">
              <Accordion.Control>
                <Group>
                  <Badge color="blue" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Code>presetScripts</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Returns the organization-authored preset scripts for the
                  current organization, sorted most-recently-created first.
                  Use <Code>presetScript(id)</Code> to load a single script
                  with its full source and parameter declarations. Built-in
                  templates are exposed via a separate{" "}
                  <Code>builtinPresets</Code> /{" "}
                  <Code>builtinPreset(id)</Code> pair &mdash; see{" "}
                  <Anchor href="#builtin-templates">Built-in Templates</Anchor>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Mutations
          </Title>
          <Accordion variant="separated" mb="md">
            <Accordion.Item value="m-create">
              <Accordion.Control>
                <Group>
                  <Badge color="green" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>createStepRuleSet(stepId, input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Create a rule set from scratch. Validates input, creates all
                  sub-entities in a transaction, and invalidates any dependent
                  cross-step rule sets.
                </Text>
                <GqlBlock>
                  {`mutation Create($stepId: UUID!, $input: CreateStepRuleSetInput!) {
  createStepRuleSet(stepId: $stepId, input: $input) {
    id
    version
  }
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-preset">
              <Accordion.Control>
                <Group>
                  <Badge color="green" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>
                    applyPresetScript(presetScriptId, tournamentStepId,
                    parameters)
                  </Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Apply a user-authored Lua preset script to a step. The
                  script runs in the sandbox and its effects (scoring rules,
                  advancement rules, structure template, cross-step rules) are
                  persisted atomically. Pass <Code>parameters</Code> as a JSON
                  string matching the parameter declarations on the preset
                  script (see the{" "}
                  <Anchor href="#lua-preset-scripts">
                    Lua Preset Scripts
                  </Anchor>{" "}
                  section).
                </Text>
                <GqlBlock>
                  {`mutation Apply(
  $presetScriptId: UUID!,
  $tournamentStepId: UUID!,
  $parameters: String   # JSON, flat { key: scalar } object
) {
  applyPresetScript(
    presetScriptId: $presetScriptId,
    tournamentStepId: $tournamentStepId,
    parameters: $parameters
  ) {
    id
    version
  }
}`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-scoring">
              <Accordion.Control>
                <Group>
                  <Badge color="green" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>scoringRulesUpdate(stepId, input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Update scoring configuration. Partial input &mdash; only
                  provided fields are updated. Increments version.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-advancement">
              <Accordion.Control>
                <Group>
                  <Badge color="green" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>advancementRulesUpdate(stepId, input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Replace all advancement rules. Deletes existing and recreates
                  from input. Increments version.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-structure">
              <Accordion.Control>
                <Group>
                  <Badge color="green" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>structureTemplateUpdate(stepId, input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Replace the structure template. Only allowed when step status
                  is <Code>CONFIGURED</Code>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-crossstep-create">
              <Accordion.Control>
                <Group>
                  <Badge color="green" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>crossStepRuleCreate(stepId, input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Add a cross-step rule. Provide either{" "}
                  <Code>sourceStepId</Code> (explicit) or{" "}
                  <Code>sourceRelative</Code> (PREVIOUS / NEXT / CURRENT).
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-crossstep-delete">
              <Accordion.Control>
                <Group>
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>crossStepRuleDelete(ruleId)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">Remove a cross-step rule by its ID.</Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-manual">
              <Accordion.Control>
                <Group>
                  <Badge color="orange" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>manualAdvancementExecute(stepId, input)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Manually advance a team to a specific game slot.
                </Text>
                <GqlBlock>
                  {`mutation ManualAdvance($stepId: UUID!, $input: ManualAdvancementInput!) {
  manualAdvancementExecute(stepId: $stepId, input: $input) { id }
}

# ManualAdvancementInput:
# teamId: UUID!
# targetGameId: UUID       (by ID)
# targetGameRef: JSONObject (or by relative ref)
# targetSlot: Int          (0-based)`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-trigger">
              <Accordion.Control>
                <Group>
                  <Badge color="orange" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>manualRuleTrigger(stepId, ruleId)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Manually trigger a rule with <Code>triggerType: MANUAL</Code>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="m-approve">
              <Accordion.Control>
                <Group>
                  <Badge color="orange" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Code>pendingActionApprove(logId)</Code>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Approve a pending action from a rule with{" "}
                  <Code>autoExecute: false</Code>. The action is then executed
                  and the log is updated.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Enums Reference
          </Title>
          <Table className={classes.statusTable} striped highlightOnHover>
            <thead>
              <tr>
                <th>Enum</th>
                <th>Values</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>ScoreAggregationType</Code>
                </td>
                <td>
                  <Code>SUM</Code>, <Code>AVERAGE</Code>, <Code>POINTS</Code>,{" "}
                  <Code>FORMULA</Code>, <Code>CUSTOM_LUA</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>RuleTriggerType</Code>
                </td>
                <td>
                  <Code>MATCH_ENDED</Code>, <Code>GAME_ENDED</Code>,{" "}
                  <Code>ROUND_ENDED</Code>, <Code>GROUP_ENDED</Code>,{" "}
                  <Code>STEP_ENDED</Code>, <Code>MANUAL</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>RuleActionType</Code>
                </td>
                <td>
                  <Code>ADVANCE</Code>, <Code>ELIMINATE</Code>,{" "}
                  <Code>END</Code>, <Code>EXECUTE_LUA</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>ScriptContextType</Code>
                </td>
                <td>
                  <Code>CONDITION</Code>, <Code>ACTION</Code>,{" "}
                  <Code>GENERATION</Code>, <Code>PRESET</Code>,{" "}
                  <Code>CROSS_STEP</Code>, <Code>FORMULA</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>PresetParameterType</Code>
                </td>
                <td>
                  <Code>INT</Code>, <Code>FLOAT</Code>, <Code>STRING</Code>,{" "}
                  <Code>BOOLEAN</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>BuiltinPresetCategory</Code>
                </td>
                <td>
                  <Code>SINGLE_ELIMINATION</Code>,{" "}
                  <Code>DOUBLE_ELIMINATION</Code>, <Code>SWISS</Code>,{" "}
                  <Code>ROUND_ROBIN</Code>, <Code>GROUP_STAGE</Code>,{" "}
                  <Code>OTHER</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>SortOrder</Code>
                </td>
                <td>
                  <Code>ASC</Code>, <Code>DESC</Code>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            In addition to the tournament-specific custom fields configured in
            tournament settings, tournaments also support the platform-wide{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            systems from the Data service.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields (Data service)
              </Text>{" "}
              — Structured, typed fields defined in Data &gt; Custom Fields.
              Supports validation, visibility, and editability controls.
              Available for <Code>Tournament</Code>, <Code>TournamentStep</Code>
              , and <Code>TournamentTeam</Code> object types.
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata
              </Text>{" "}
              — Flexible key-value store for attaching arbitrary data. Each
              entry has a type (String, Boolean, URL, Image URL) and visibility
              (Public or Private).
            </List.Item>
          </List>
          <Text mb="sm">
            Manage these in the tournament&apos;s settings screens (Custom Field
            Values and Metadata tabs) or via the GraphQL API:
          </Text>
          <GqlBlock>
            {`# Query custom field values for a tournament
query ($objectType: String!, $objectId: ID!) {
  customFieldValues(objectType: $objectType, objectId: $objectId) {
    definition { key, name, type, required }
    value
  }
}
# objectType: "Tournament", "TournamentStep", or "TournamentTeam"

# Query metadata for a tournament
query ($objectType: String!, $objectId: ID!) {
  objectMetadata(objectType: $objectType, objectId: $objectId) {
    key, value, type, visibility
  }
}`}
          </GqlBlock>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light" mt="md">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, including how to
            create definitions, set values, and manage metadata entries.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
